import {
  featureHasPipelineProps,
} from '@buxus/shared'
import type { Feature, FeatureCollection, Geometry, Polygon } from 'geojson'
import { mdiCrosshairs, mdiCrosshairsGps } from '@mdi/js'
import mapboxgl from 'mapbox-gl'
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  attachedHighlightFeatures,
  keyframeOverlayFeatures,
  lineFeatures,
  objectAnchorLngLat,
  objectLineSegments,
  orderedPoseKeyframes,
  plumeFeatures,
  PLUME_PAINT,
  zoneFeatures,
} from './geo'
import { posesSameSpot } from './format'
import { HAZARD_TYPES, VEHICLE_TYPES, iconSvg, iconVariantFor } from './icons'
import { presenceToneForInitials } from './presenceAvatar'
import { paletteColor, paletteLabel } from './palette'
import { poseFromKeyframes } from './resolveSession'
import { TYPE_SIZE_PX } from './toolUi'
import type { ObjectSize, ResolvedObject } from './types'

const SRC_ZONES = 'sc-zones'
const SRC_PLUMES = 'sc-plumes'
const SRC_LINES = 'sc-lines'
const SRC_HL = 'sc-pipe-hl'
const SRC_HL_HOT = 'sc-pipe-hl-hot'
const SRC_FOCUS = 'sc-focus'
const SRC_DRAW = 'sc-draw'
/** Personal scribble pad — local only, not board objects. */
const SRC_SCRIBBLE = 'sc-scribble'
const SRC_KF = 'sc-keyframes'

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] }
const EMPTY_IDS: readonly string[] = []

function sameIdList(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined,
): boolean {
  if (a === b) return true
  const left = a ?? EMPTY_IDS
  const right = b ?? EMPTY_IDS
  if (left.length === 0 && right.length === 0) return true
  if (left.length !== right.length) return false
  for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) return false
  return true
}

function sameWatchCursors(
  a: ScenarioMapHostProps['watchCursors'],
  b: ScenarioMapHostProps['watchCursors'],
): boolean {
  if (a === b) return true
  const left = a ?? []
  const right = b ?? []
  if (left.length === 0 && right.length === 0) return true
  if (left.length !== right.length) return false
  for (let i = 0; i < left.length; i++) {
    const x = left[i]!
    const y = right[i]!
    if (
      x.id !== y.id ||
      x.lng !== y.lng ||
      x.lat !== y.lat ||
      x.down !== y.down ||
      x.clickSeq !== y.clickSeq ||
      x.hoverId !== y.hoverId ||
      x.cursor !== y.cursor ||
      x.who !== y.who
    ) {
      return false
    }
  }
  return true
}

function sameDirectedCamera(
  a: DirectedCamera | null | undefined,
  b: DirectedCamera | null | undefined,
): boolean {
  if (a === b) return true
  if (!a || !b) return !a && !b
  return (
    a.beatKey === b.beatKey &&
    a.lng === b.lng &&
    a.lat === b.lat &&
    a.zoom === b.zoom &&
    a.easeMs === b.easeMs &&
    a.instant === b.instant &&
    a.softHold === b.softHold
  )
}

/** Skip map work when only parent chrome changed (spine tabs, clocks, rail). Callbacks live on refs. */
function mapHostPropsEqual(prev: ScenarioMapHostProps, next: ScenarioMapHostProps): boolean {
  return (
    prev.mapId === next.mapId &&
    prev.styleUrl === next.styleUrl &&
    prev.accessToken === next.accessToken &&
    prev.camera === next.camera &&
    sameDirectedCamera(prev.directedCamera, next.directedCamera) &&
    sameWatchCursors(prev.watchCursors, next.watchCursors) &&
    prev.watchFocusId === next.watchFocusId &&
    prev.remoteDragId === next.remoteDragId &&
    prev.objects === next.objects &&
    prev.selectedId === next.selectedId &&
    sameIdList(prev.highlightIds, next.highlightIds) &&
    sameIdList(prev.iconHighlightIds, next.iconHighlightIds) &&
    sameIdList(prev.ghostFlashIds, next.ghostFlashIds) &&
    prev.iconsOnly === next.iconsOnly &&
    prev.surfacePlumes === next.surfacePlumes &&
    prev.viewResetId === next.viewResetId &&
    prev.placeArmed === next.placeArmed &&
    prev.freehandArmed === next.freehandArmed &&
    prev.penArmed === next.penArmed &&
    prev.rulerArmed === next.rulerArmed &&
    prev.scribbleArmed === next.scribbleArmed &&
    prev.scribbleVisible === next.scribbleVisible &&
    prev.scribbleStrokes === next.scribbleStrokes &&
    prev.sketchStyle === next.sketchStyle &&
    prev.editing === next.editing &&
    prev.showAuthorGuides === next.showAuthorGuides &&
    prev.waypointObjectId === next.waypointObjectId &&
    prev.plumeFocusObjectId === next.plumeFocusObjectId &&
    prev.hideWaypointUnitIcon === next.hideWaypointUnitIcon &&
    prev.waypointFocusIndex === next.waypointFocusIndex &&
    prev.waypointsEditable === next.waypointsEditable &&
    prev.pathHoldLabel === next.pathHoldLabel &&
    prev.mapAssetChip === next.mapAssetChip &&
    prev.mapFeatureHighlight === next.mapFeatureHighlight &&
    prev.attachedHighlightObjects === next.attachedHighlightObjects &&
    prev.linkHighlightSelected === next.linkHighlightSelected &&
    prev.focusDim === next.focusDim &&
    sameIdList(prev.focusDimKeep, next.focusDimKeep) &&
    prev.cameraFollowOn === next.cameraFollowOn &&
    prev.showAutoTrack === next.showAutoTrack &&
    prev.samplePlayheadIso === next.samplePlayheadIso
  )
}

/**
 * Magnified (~3×) system cursors. Hotspot sits on the presenter's tip/palm.
 * Pointer = pointing hand (index up + three folded knuckles + thumb).
 * Grab hands share one palm so folding fingers does not jump the glyph.
 */
/** Fill uses currentColor so the leader’s presence tone can tint the hand/pointer. */
const WATCH_POINTER = `<svg class="sc-watch-glyph" viewBox="0 0 28 36" width="42" height="54" aria-hidden="true"><path fill="currentColor" stroke="#111" stroke-width="1.2" stroke-linejoin="round" d="M11.4 1.3c1.05 0 1.9.85 1.9 1.9v12.4h2.1c.7 0 1.25.55 1.25 1.25v1.15c0 .7-.55 1.25-1.25 1.25h-2.1v.35h2.45c.7 0 1.25.55 1.25 1.25v1.1c0 .7-.55 1.25-1.25 1.25h-2.45v.35h2.15c.7 0 1.25.55 1.25 1.25v1.2c0 .7-.55 1.25-1.25 1.25h-1.7c.05 2.85-1.95 5.05-5.05 5.05H6.6c-2.6 0-4.55-1.65-5.25-4L.25 21.4c-.35-1 .25-2.05 1.3-2.25.5-.1 1.05.15 1.35.55l1.7 2.25V6.5c0-1.25 1-2.25 2.25-2.1.65.1 1.15.65 1.3 1.25l.25 1.25v9.1h1.2V4.3c0-1.2.95-2.15 2.15-2.15h1.4z"/><path fill="none" stroke="#111" stroke-width="1.05" stroke-linecap="round" d="M15.5 15.55h2M15.5 18.85h2.35M15.5 22.1h2"/></svg>`
const WATCH_POINTER_OFFSET: [number, number] = [-18, -3]
const WATCH_GRAB = `<svg class="sc-watch-glyph" viewBox="0 0 32 32" width="48" height="48" aria-hidden="true"><path fill="currentColor" stroke="#111" stroke-width="1.25" stroke-linejoin="round" d="M11.2 11.5V6.2a1.7 1.7 0 0 1 3.4 0v5.3m.3-.4V4.6a1.7 1.7 0 0 1 3.4 0V11m.35-.3V6.6a1.7 1.7 0 0 1 3.4 0v6.2m.3-.4V8.2a1.7 1.7 0 0 1 3.4 0V20c0 4.3-2.8 6.7-6.6 6.7h-2.2c-3.1 0-5.2-1.5-6-4.3l-1.8-4.5a1.75 1.75 0 0 1 2.95-1.7l.9.8V11.5z"/></svg>`
const WATCH_GRABBING = `<svg class="sc-watch-glyph" viewBox="0 0 32 32" width="48" height="48" aria-hidden="true"><path fill="currentColor" stroke="#111" stroke-width="1.25" stroke-linejoin="round" d="M10.6 14.2v-1.5a1.55 1.55 0 0 1 3.1 0v1.5m.15-.8v-1.6a1.55 1.55 0 0 1 3.1 0v1.6m.2-.3v-1.3a1.55 1.55 0 0 1 3.1 0v2.1m.15-.15v-.8a1.55 1.55 0 0 1 3.1 0V20c0 3.8-2.5 6-5.9 6h-1.8c-2.7 0-4.5-1.3-5.3-3.6l-1.5-3.6a1.65 1.65 0 0 1 2.65-1.8l.9.8v-3z"/></svg>`
const WATCH_HAND_OFFSET: [number, number] = [-22, -20]

/** OSM / Mapbox credit line (“OpenStreetMap · Improve this map”). Flip on before a public map. */
const SHOW_MAP_ATTRIBUTION = false
/** Mapbox mark in the bottom-left corner. Same switch as the credit line. */
const SHOW_MAPBOX_LOGO = false

/** Icon captions (Engine, LE, …) appear at/above this zoom unless selected or timeline-lit. */
const CAPTION_MIN_ZOOM = 14.85

export type MapFeatureTap = {
  kind: 'pipeline' | 'road'
  properties: Record<string, unknown>
  path: [number, number][]
  segments: [number, number][][]
  lng: number
  lat: number
  name: string
  /** Distance from the query point to the nearest vertex on the feature (meters). */
  distanceM?: number
  /** Tile feature id when the tap came from a precise map click. */
  featureId?: string | number
}

export type ScenarioMapApi = {
  queryNearbyFeatures: (lng: number, lat: number, radiusM?: number) => MapFeatureTap[]
  /**
   * Re-resolve path/segments for a linked asset from currently loaded Mapbox tiles
   * (by feature identity). Returns null when the feature is not in the current view.
   */
  resolveAttachedGeometry: (
    kind: 'pipeline' | 'road',
    properties: Record<string, unknown>,
  ) => { path: [number, number][]; segments: [number, number][][] } | null
  /** Live Mapbox center/zoom (user pan/zoom), not session.camera. May include in-progress drag. */
  getCamera: () => {
    lng: number
    lat: number
    zoom: number
    dragId?: string
    dragLng?: number
    dragLat?: number
  } | null
  resize: () => void
  jumpToCamera: (cam: { lng: number; lat: number; zoom: number }) => void
  easeToCamera: (cam: { lng: number; lat: number; zoom: number }, durationMs?: number) => void
  /**
   * Light pan only if the point sits outside a padded viewport inset.
   * No-op when already comfortably in view.
   */
  ensureLngLatInView: (
    lng: number,
    lat: number,
    opts?: { marginPx?: number; durationMs?: number },
  ) => void
  /**
   * Zoom out only when a point sits outside the canvas, keeping the current view in frame.
   * Returns whether the camera moved.
   */
  expandToInclude: (points: readonly { lng: number; lat: number }[]) => boolean
  /**
   * Frame these points. Zooms out to include them; never zooms in past the current zoom.
   * Returns whether a move was requested.
   */
  fitPoints: (points: readonly { lng: number; lat: number }[]) => boolean
}

function bindMapApi(
  map: mapboxgl.Map,
  programmaticMoveRef: { current: boolean },
  /** Optional live drag pose for Share flush on pointer-up (same source as onCameraFrame). */
  getLiveDrag?: () => {
    dragId?: string
    dragLng?: number
    dragLat?: number
  } | null,
): ScenarioMapApi {
  const withProgrammatic = (fn: () => void): void => {
    programmaticMoveRef.current = true
    try {
      fn()
    } finally {
      window.setTimeout(() => {
        programmaticMoveRef.current = false
      }, 450)
    }
  }
  return {
    queryNearbyFeatures: (lng, lat, radiusM) => queryNearbyFeatures(map, lng, lat, radiusM),
    resolveAttachedGeometry: (kind, properties) => {
      const key = relatedKey(kind, properties)
      if (!key) return null
      const segments = collectSegments(map, kind, properties, null)
      if (!segments.length) return null
      const path = [...segments].sort((a, b) => b.length - a.length)[0] ?? []
      return { path, segments }
    },
    getCamera: () => {
      const c = map.getCenter()
      const drag = getLiveDrag?.() ?? null
      return {
        lng: c.lng,
        lat: c.lat,
        zoom: map.getZoom(),
        ...(drag?.dragId && drag.dragLng != null && drag.dragLat != null
          ? {
              dragId: drag.dragId,
              dragLng: drag.dragLng,
              dragLat: drag.dragLat,
            }
          : {}),
      }
    },
    resize: () => {
      map.resize()
    },
    jumpToCamera: (cam) => {
      withProgrammatic(() => {
        map.jumpTo({ center: [cam.lng, cam.lat], zoom: cam.zoom })
      })
    },
    easeToCamera: (cam, durationMs = 1000) => {
      withProgrammatic(() => {
        map.stop()
        map.easeTo({
          center: [cam.lng, cam.lat],
          zoom: cam.zoom,
          duration: Math.max(0, durationMs),
          essential: true,
        })
      })
    },
    ensureLngLatInView: (lng, lat, opts) => {
      const margin = Math.max(24, opts?.marginPx ?? 72)
      const duration = Math.max(0, opts?.durationMs ?? 380)
      const canvas = map.getCanvas()
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (w < margin * 2 || h < margin * 2) return
      const pt = map.project([lng, lat])
      // Minimal pan to keep the point inside the margin box — never hard-center.
      // (Centering when barely off-canvas caused wild swings.)
      let dx = 0
      let dy = 0
      if (pt.x < margin) dx = margin - pt.x
      else if (pt.x > w - margin) dx = w - margin - pt.x
      if (pt.y < margin) dy = margin - pt.y
      else if (pt.y > h - margin) dy = h - margin - pt.y
      if (dx === 0 && dy === 0) return
      const centerPx = map.project(map.getCenter())
      const next = map.unproject([centerPx.x - dx, centerPx.y - dy])
      withProgrammatic(() => {
        map.stop()
        map.easeTo({
          center: [next.lng, next.lat],
          duration,
          essential: true,
        })
      })
    },
    expandToInclude: (points) => {
      if (!points.length) return false
      const canvas = map.getCanvas()
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      const outside = points.some((p) => {
        const pt = map.project([p.lng, p.lat])
        return pt.x < 8 || pt.y < 8 || pt.x > w - 8 || pt.y > h - 8
      })
      if (!outside) return false
      const bounds = map.getBounds()
      if (!bounds) return false
      for (const p of points) bounds.extend([p.lng, p.lat])
      withProgrammatic(() => {
        map.stop()
        map.fitBounds(bounds, {
          padding: 64,
          duration: 280,
          maxZoom: map.getZoom(),
          essential: true,
        })
      })
      return true
    },
    fitPoints: (points) => {
      if (!points.length) return false
      const canvas = map.getCanvas()
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      const pad = 48
      const outside = points.some((p) => {
        const pt = map.project([p.lng, p.lat])
        return pt.x < pad || pt.y < pad || pt.x > w - pad || pt.y > h - pad
      })
      if (!outside) return false
      const bounds = new mapboxgl.LngLatBounds(
        [points[0]!.lng, points[0]!.lat],
        [points[0]!.lng, points[0]!.lat],
      )
      for (const p of points) bounds.extend([p.lng, p.lat])
      const maxZoom = map.getZoom()
      withProgrammatic(() => {
        map.stop()
        map.fitBounds(bounds, {
          padding: 80,
          duration: 280,
          maxZoom,
          essential: true,
        })
      })
      return true
    },
  }
}

/** @deprecated use MapFeatureTap */
export type PipelineTap = MapFeatureTap

type DirectedCamera = {
  lng: number
  lat: number
  zoom: number
  /** Stable id for the active beat — change triggers move. */
  beatKey: string
  easeMs: number
  /** Scrub / seek: jump instead of ease. */
  instant?: boolean
  /**
   * Parent soft-hold while reframing this beat for Save camera.
   * Skip apply (keep live view) but still block foundation camera reset.
   */
  softHold?: boolean
}

type ScenarioMapHostProps = {
  mapId: string
  /** Style URL for this plate. Callers pass the session row, not a catalog lookup. */
  styleUrl: string
  /** Token that can request styleUrl. */
  accessToken: string
  camera: { lng: number; lat: number; zoom: number }
  /** When set (scripted follow), ease/jump to this beat. Null = user-driven. */
  directedCamera?: DirectedCamera | null
  /** Fired on real user pan/zoom/rotate/pitch (not programmatic moves). */
  onUserCameraGesture?: () => void
  /** Live center and zoom while the user is moving the map. Skips programmatic moves. Pointer is included when the cursor is over the map. */
  onCameraFrame?: (cam: {
    lng: number
    lat: number
    zoom: number
    pointerLng?: number
    pointerLat?: number
    pointerDown?: boolean
    clickSeq?: number
    hoverId?: string
    hoverTip?: string
    grab?: boolean
    cursor?: 'pointer' | 'grab' | 'grabbing'
    focusId?: string
    /** In-progress drag. Viewers place the object from this instead of waiting on the save. */
    dragId?: string
    dragLng?: number
    dragLat?: number
  }) => void
  /** Remote Watch cursors (one per sharer). Drawn on this map; not sent back out. */
  watchCursors?: readonly {
    /** Stable key (usually sharer email). */
    id: string
    lng: number
    lat: number
    down?: boolean
    clickSeq?: number
    hoverId?: string
    hoverTip?: string
    grab?: boolean
    cursor?: 'pointer' | 'grab' | 'grabbing'
    zoomCue?: 'in' | 'out'
    zoomCueN?: number
    /** Initials — also the color key (`presenceToneForInitials`). */
    who?: string
  }[]
  /** Presenter's clicked object. Rim and zone highlight, kept when the pointer leaves the map. */
  watchFocusId?: string | null
  /** Object someone else is dragging right now. Rim only; not a local selection. */
  remoteDragId?: string | null
  objects: readonly ResolvedObject[]
  selectedId: string | null
  highlightIds: readonly string[]
  /**
   * Extra ids that light map icons only (not zones / roads / linked-asset HL).
   * Used for Roleplay roster hover.
   */
  iconHighlightIds?: readonly string[]
  /** Hold-G reveal: cyan rings on objects this view would otherwise hide. */
  ghostFlashIds?: readonly string[]
  /** G/Z hold: icons only. Hides plumes, zones, roads, and Type/Label text. */
  iconsOnly?: boolean
  /** Personal display pref. When false, no plumes draw. */
  surfacePlumes?: boolean
  viewResetId: number
  placeArmed: boolean
  freehandArmed: boolean
  /** Click vertices; double-click finishes a sketch path. */
  penArmed?: boolean
  /** Ephemeral distance measure (not persisted). */
  rulerArmed?: boolean
  /**
   * Personal scratch pad (hotkey). Local strokes only — not shared Freehand /
   * not Instant Replay. When armed, layer is visible and drawable.
   */
  scribbleArmed?: boolean
  /**
   * Show committed scribbles. Off = hide layer (strokes kept in React state).
   * Defaults to scribbleArmed when omitted.
   */
  scribbleVisible?: boolean
  /** Committed personal scribbles to paint under the draft stroke. */
  scribbleStrokes?: readonly { id: string; path: [number, number][] }[]
  sketchStyle: { color: string; width: number; opacity: number }
  editing: boolean
  /** When set, overrides global `editing` for which markers are Mapbox-draggable. */
  canDragObject?: (id: string) => boolean
  /** Owner/Editor role — cursor coords (includes template preview). */
  showAuthorGuides: boolean
  /**
   * When set (Inspect + Owner/Editor + Waypoints open), plot that object's pose keyframes as waypoints.
   * Null clears the overlay. Also suppresses other objects' radii / roads / sketches.
   */
  waypointObjectId?: string | null
  /**
   * When set, only this object's surface-wind plume is drawn (e.g. tornado Position edit/preview).
   * Null keeps every visible plume source.
   */
  plumeFocusObjectId?: string | null
  /** Hide unit icon while editing pins (accordion open with at least one waypoint). */
  hideWaypointUnitIcon?: boolean
  /** Highlighted waypoint index (0-based, sorted order). */
  waypointFocusIndex?: number | null
  waypointsEditable?: boolean
  onWaypointFocus?: (index: number | null) => void
  onWaypointMove?: (index: number, lng: number, lat: number) => void
  /** Preview path only — e.g. "Hold 4:32". Hidden during normal Run. */
  pathHoldLabel?: string | null
  onSelectObject: (id: string | null) => void
  onPlace: (lng: number, lat: number, feature?: MapFeatureTap | null) => void
  onStroke: (path: [number, number][]) => void
  /** Personal scribble commit (session-local; not a board object). */
  onScribbleStroke?: (path: [number, number][]) => void
  /** Ruler draft stats; null when cleared / disarmed. */
  onRulerChange?: (stats: { points: number; totalM: number; lastM: number } | null) => void
  onMoveObject: (id: string, lng: number, lat: number, from?: { lng: number; lat: number }) => void
  onPipelineTap: (tap: MapFeatureTap | null) => void
  /**
   * Precise click on basemap pipelines after board hits miss.
   * Empty array means this click is not an asset peek (clear the chip).
   */
  onBasemapPipelines?: (
    hits: MapFeatureTap[],
    point: { x: number; y: number },
  ) => void
  /** Chip anchored at the click. Null hides it. Does not change the cursor. */
  mapAssetChip?: {
    lng: number
    lat: number
    name: string
    operator: string
    count: number
  } | null
  onMapAssetDetails?: () => void
  onMapAssetDismiss?: () => void
  /** When set, shows that map-feature geometry (legacy; prefer attachedHighlightObjects). */
  mapFeatureHighlight?: MapFeatureTap | null
  /** Attached asset geometries for the current selection / hover group. */
  attachedHighlightObjects?: readonly { params: Record<string, unknown> }[]
  /**
   * When true, the selected marker uses the cyan linked-asset highlight ring
   * (Inspect + linked/find highlights visible).
   */
  linkHighlightSelected?: boolean
  /**
   * Soft lightbox: darken the basemap/overlays under highlights so the selected
   * object and linked assets read more clearly (Inspect link focus).
   */
  focusDim?: boolean
  /** Layer ids that stay bright while focusDim is on. Tornado preview keeps the width ring. */
  focusDimKeep?: readonly string[]
  /** Plume under the cursor: surface, tornado, both, or none. */
  onPlumeHover?: (kind: 'surface' | 'tornado' | 'hurricane' | 'both' | null) => void
  mapApiRef?: { current: ScenarioMapApi | null }
  /** Fired when Mapbox finishes loading tiles / settling (use to re-resolve linked geometry). */
  onMapIdle?: () => void
  /** Fired after a marker drag ends — flush persistence without waiting for debounce. */
  onObjectMoveEnd?: () => void
  /** Story-camera follow toggle (map control under zoom). */
  cameraFollowOn?: boolean
  onCameraFollowChange?: (on: boolean) => void
  /** When false, hide the Auto-track map control (hub / wizard backdrop). */
  showAutoTrack?: boolean
  /**
   * When set, MapHost samples this playhead every animation frame and moves any
   * keyframed marker via poseFromKeyframes (React object props stay lower-rate).
   */
  samplePlayheadIso?: () => string | null
}

/** Off = hollow crosshair (free cam). On = filled bullseye (story cam locked). */
function syncAutoTrackButton(
  btn: HTMLButtonElement,
  on: boolean,
  visible: boolean,
): void {
  btn.hidden = !visible
  btn.classList.toggle('is-on', on)
  btn.setAttribute('aria-pressed', on ? 'true' : 'false')
  btn.setAttribute('aria-label', on ? 'Auto-track on' : 'Auto-track off')
  btn.title = on
    ? 'Auto-track on - follows story camera (T)'
    : 'Auto-track off - you control pan and zoom (T)'
  const path = btn.querySelector('path')
  if (path) path.setAttribute('d', on ? mdiCrosshairsGps : mdiCrosshairs)
}

/** Append Auto-track under +/− in the same Mapbox ctrl group (compass slot). */
function attachAutoTrackControl(
  map: mapboxgl.Map,
  opts: {
    getOn: () => boolean
    getVisible: () => boolean
    onToggle: () => void
    buttonRef: { current: HTMLButtonElement | null }
  },
): () => void {
  const group = map
    .getContainer()
    .querySelector('.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-group')
  if (!(group instanceof HTMLElement)) return () => {}

  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'mapboxgl-ctrl-autotrack'
  btn.innerHTML = `<span class="mapboxgl-ctrl-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="${mdiCrosshairs}"/></svg></span>`
  syncAutoTrackButton(btn, opts.getOn(), opts.getVisible())

  const onClick = (e: MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    opts.onToggle()
    // Mouse activate should not leave Mapbox's focus ring stuck on the button.
    btn.blur()
  }
  btn.addEventListener('click', onClick)
  group.appendChild(btn)
  opts.buttonRef.current = btn

  return () => {
    btn.removeEventListener('click', onClick)
    btn.remove()
    if (opts.buttonRef.current === btn) opts.buttonRef.current = null
  }
}

const lineLayerIdsByMap = new WeakMap<mapboxgl.Map, string[]>()

function computeLineLayerIds(map: mapboxgl.Map): string[] {
  return (map.getStyle()?.layers ?? [])
    .filter((l) => l.type === 'line' && !String(l.id).startsWith('sc-'))
    .map((l) => l.id)
}

/** Cached so a click does not serialize the whole style via getStyle(). */
function lineLayerIds(map: mapboxgl.Map): string[] {
  const cached = lineLayerIdsByMap.get(map)
  if (cached) return cached
  const ids = computeLineLayerIds(map)
  lineLayerIdsByMap.set(map, ids)
  return ids
}

function rememberLineLayerIds(map: mapboxgl.Map): void {
  lineLayerIdsByMap.set(map, computeLineLayerIds(map))
}

function hotHighlightData(hit: MapFeatureTap | null): FeatureCollection {
  if (!hit) return EMPTY_FC
  const geom =
    hit.segments.length > 1
      ? { type: 'MultiLineString' as const, coordinates: hit.segments }
      : hit.path.length >= 2
        ? { type: 'LineString' as const, coordinates: hit.path }
        : null
  return {
    type: 'FeatureCollection',
    features: geom ? [{ type: 'Feature', properties: {}, geometry: geom }] : [],
  }
}

/** Paint the clicked line now. The React effect writes the same geometry after commit. */
function applyHotHighlight(map: mapboxgl.Map, hit: MapFeatureTap | null): void {
  setGeo(map, SRC_HL_HOT, hotHighlightData(hit))
}

function lineCoords(geom: Geometry | null | undefined): [number, number][][] {
  if (!geom) return []
  if (geom.type === 'LineString') return [geom.coordinates as [number, number][]]
  if (geom.type === 'MultiLineString') return geom.coordinates as [number, number][][]
  return []
}

function featureGeometry(hit: mapboxgl.GeoJSONFeature): Geometry | null {
  const json = typeof hit.toJSON === 'function' ? hit.toJSON() : hit
  return (json.geometry as Geometry) ?? null
}

function isRoadFeature(hit: mapboxgl.GeoJSONFeature): boolean {
  if (featureHasPipelineProps(hit)) return false
  const layerId = String(hit.layer?.id ?? '')
  if (/road|street|bridge|tunnel|path|motorway|highway|rail/i.test(layerId)) return true
  const props = hit.properties ?? {}
  const cls = String(props.class ?? props.type ?? '')
  if (/street|primary|secondary|tertiary|motorway|trunk|service|residential|path|track|pedestrian/i.test(cls)) {
    return true
  }
  return Boolean(props.name && hit.layer?.type === 'line')
}

function tapName(kind: 'pipeline' | 'road', props: Record<string, unknown>): string {
  if (kind === 'pipeline') {
    return String(props.SYS_NAME || props.CO_NAME || props.PRODUCT || 'Pipeline')
  }
  return String(props.name || props.name_en || props.class || 'Road')
}

function relatedKey(kind: 'pipeline' | 'road', props: Record<string, unknown>): string | null {
  if (kind === 'pipeline') {
    const id = props.BUXUS_ID ?? props.SYS_NAME
    return id != null && String(id).trim() ? String(id) : null
  }
  const name = props.name ?? props.name_en
  return name != null && String(name).trim() ? String(name) : null
}

function collectSegments(
  map: mapboxgl.Map,
  kind: 'pipeline' | 'road',
  props: Record<string, unknown>,
  seedGeom: Geometry | null,
): [number, number][][] {
  const segs = lineCoords(seedGeom)
  const key = relatedKey(kind, props)
  const ids = lineLayerIds(map)
  let hits: mapboxgl.GeoJSONFeature[] = []
  try {
    hits = ids.length
      ? (map.queryRenderedFeatures({ layers: ids }) as mapboxgl.GeoJSONFeature[])
      : (map.queryRenderedFeatures() as mapboxgl.GeoJSONFeature[])
  } catch {
    hits = []
  }
  for (const hit of hits) {
    const hp = { ...(hit.properties ?? {}) } as Record<string, unknown>
    const match =
      kind === 'pipeline'
        ? featureHasPipelineProps(hit) && key != null && relatedKey('pipeline', hp) === key
        : isRoadFeature(hit) && key != null && relatedKey('road', hp) === key
    if (!match) continue
    segs.push(...lineCoords(featureGeometry(hit)))
  }
  const seen = new Set<string>()
  const unique: [number, number][][] = []
  for (const seg of segs) {
    if (seg.length < 2) continue
    const sig = seg.map((p) => `${p[0].toFixed(5)},${p[1].toFixed(5)}`).join('|')
    if (seen.has(sig)) continue
    seen.add(sig)
    unique.push(seg)
  }
  return unique
}

function haversineM(aLng: number, aLat: number, bLng: number, bLat: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const lat1 = toRad(aLat)
  const lat2 = toRad(bLat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)))
}

function minDistToSegmentsM(
  lng: number,
  lat: number,
  segments: [number, number][][],
): number {
  let best = Number.POSITIVE_INFINITY
  for (const seg of segments) {
    for (const [x, y] of seg) {
      best = Math.min(best, haversineM(lng, lat, x, y))
    }
  }
  return best
}

/** Meters from a point to the closest place on a segment, not just its vertices. */
function distToSegmentM(
  lng: number,
  lat: number,
  a: [number, number],
  b: [number, number],
): number {
  const latRad = (lat * Math.PI) / 180
  const kx = 111_320 * Math.max(0.2, Math.cos(latRad))
  const ky = 111_320
  const px = lng * kx
  const py = lat * ky
  const ax = a[0] * kx
  const ay = a[1] * ky
  const bx = b[0] * kx
  const by = b[1] * ky
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - ax, py - ay)
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

function closestSegment(
  lng: number,
  lat: number,
  segments: [number, number][][],
): { segment: [number, number][]; distanceM: number } {
  let segment = segments[0] ?? []
  let distanceM = Number.POSITIVE_INFINITY
  for (const seg of segments) {
    let dist = Number.POSITIVE_INFINITY
    for (let i = 1; i < seg.length; i++) {
      dist = Math.min(dist, distToSegmentM(lng, lat, seg[i - 1]!, seg[i]!))
    }
    if (dist < distanceM) {
      distanceM = dist
      segment = seg
    }
  }
  return { segment, distanceM }
}

const PIPELINE_CLICK_PAD_PX = 12

/** Pipelines under a click. This feature's geometry only — not a viewport-wide segment scan. */
function queryPipelinesAtPoint(
  map: mapboxgl.Map,
  point: mapboxgl.Point,
  lng: number,
  lat: number,
): MapFeatureTap[] {
  const pad = PIPELINE_CLICK_PAD_PX
  const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [point.x - pad, point.y - pad],
    [point.x + pad, point.y + pad],
  ]
  const ids = lineLayerIds(map)
  if (!ids.length) return []
  let hits: mapboxgl.GeoJSONFeature[] = []
  try {
    hits = map.queryRenderedFeatures(bbox, { layers: ids }) as mapboxgl.GeoJSONFeature[]
  } catch {
    return []
  }
  const seen = new Set<string>()
  const out: MapFeatureTap[] = []
  for (const hit of hits) {
    if (!featureHasPipelineProps(hit)) continue
    const json = typeof hit.toJSON === 'function' ? hit.toJSON() : hit
    const properties = { ...(json.properties ?? {}) } as Record<string, unknown>
    const key = relatedKey('pipeline', properties) ?? tapName('pipeline', properties)
    if (seen.has(key)) continue
    seen.add(key)
    const segments = lineCoords(featureGeometry(hit))
    if (!segments.length) continue
    // The tile feature can include other pieces of the same line. Light the one under the click.
    const nearest = closestSegment(lng, lat, segments)
    if (nearest.segment.length < 2) continue
    const featureId =
      hit.id != null && (typeof hit.id === 'string' || typeof hit.id === 'number')
        ? hit.id
        : undefined
    out.push({
      kind: 'pipeline',
      properties,
      path: nearest.segment,
      segments: [nearest.segment],
      lng,
      lat,
      name: tapName('pipeline', properties),
      distanceM: nearest.distanceM,
      featureId,
    })
  }
  out.sort((a, b) => (a.distanceM ?? 0) - (b.distanceM ?? 0))
  return out
}

function queryNearbyFeatures(
  map: mapboxgl.Map,
  lng: number,
  lat: number,
  radiusM = 200,
): MapFeatureTap[] {
  const latRad = (lat * Math.PI) / 180
  const dLat = radiusM / 111_320
  const dLng = radiusM / (111_320 * Math.max(0.2, Math.cos(latRad)))
  const sw = map.project([lng - dLng, lat - dLat])
  const ne = map.project([lng + dLng, lat + dLat])
  const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [Math.min(sw.x, ne.x), Math.min(sw.y, ne.y)],
    [Math.max(sw.x, ne.x), Math.max(sw.y, ne.y)],
  ]
  const ids = lineLayerIds(map)
  let hits: mapboxgl.GeoJSONFeature[] = []
  try {
    hits = ids.length
      ? (map.queryRenderedFeatures(bbox, { layers: ids }) as mapboxgl.GeoJSONFeature[])
      : (map.queryRenderedFeatures(bbox) as mapboxgl.GeoJSONFeature[])
  } catch {
    return []
  }
  const seen = new Set<string>()
  const out: { tap: MapFeatureTap; dist: number }[] = []
  for (const hit of hits) {
    const pipe = featureHasPipelineProps(hit)
    const road = !pipe && isRoadFeature(hit)
    if (!pipe && !road) continue
    const kind: 'pipeline' | 'road' = pipe ? 'pipeline' : 'road'
    const json = typeof hit.toJSON === 'function' ? hit.toJSON() : hit
    const properties = { ...(json.properties ?? {}) } as Record<string, unknown>
    const key = `${kind}:${relatedKey(kind, properties) ?? tapName(kind, properties)}`
    if (seen.has(key)) continue
    seen.add(key)
    const geometry = (json.geometry as Geometry) ?? null
    const segments = collectSegments(map, kind, properties, geometry)
    if (!segments.length) continue
    const dist = minDistToSegmentsM(lng, lat, segments)
    if (dist > radiusM) continue
    const path = [...segments].sort((a, b) => b.length - a.length)[0] ?? []
    out.push({
      dist,
      tap: {
        kind,
        properties,
        path,
        segments,
        lng,
        lat,
        name: tapName(kind, properties),
        distanceM: dist,
      },
    })
  }
  return out.sort((a, b) => a.dist - b.dist).map((x) => x.tap)
}

function ensureOverlayLayers(map: mapboxgl.Map): void {
  if (!map.getSource(SRC_PLUMES)) {
    map.addSource(SRC_PLUMES, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-plumes-fill',
      type: 'fill',
      source: SRC_PLUMES,
      paint: {
        'fill-color': [
          'match',
          ['get', 'windSource'],
          'surface',
          PLUME_PAINT.surface.fill,
          'hurricane',
          PLUME_PAINT.hurricane.fill,
          PLUME_PAINT.tornado.fill,
        ],
        'fill-opacity': 0.5,
      },
    })
    map.addLayer({
      id: 'sc-plumes-line',
      type: 'line',
      source: SRC_PLUMES,
      paint: {
        'line-color': [
          'match',
          ['get', 'windSource'],
          'surface',
          PLUME_PAINT.surface.line,
          'hurricane',
          PLUME_PAINT.hurricane.line,
          PLUME_PAINT.tornado.line,
        ],
        'line-width': 2,
        'line-opacity': 0.9,
      },
    })
  }
  if (!map.getSource(SRC_ZONES)) {
    map.addSource(SRC_ZONES, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-zones-fill',
      type: 'fill',
      source: SRC_ZONES,
      layout: {
        'fill-sort-key': ['case', ['any', ['==', ['get', 'kind'], 'tornado-dir'], ['==', ['get', 'kind'], 'hurricane-dir']], 2, 1],
      },
      paint: {
        'fill-color': [
          'match',
          ['get', 'kind'],
          'fire',
          '#FF4D00',
          'brwn',
          '#8B5A2B',
          'tornado-dir',
          '#E4D4FF',
          'hurricane-dir',
          '#D6E6FA',
          'tornado',
          '#A56BFF',
          'hurricane',
          '#3B7DD8',
          '#FF8C1A',
        ],
        'fill-opacity': [
          'case',
          ['any', ['==', ['get', 'kind'], 'tornado-dir'], ['==', ['get', 'kind'], 'hurricane-dir']],
          0.95,
          ['==', ['get', 'selected'], 1],
          ['match', ['get', 'kind'], 'fire', 0.52, 'brwn', 0.5, 'tornado', 0.46, 'hurricane', 0.48, 0.48],
          ['==', ['get', 'lit'], 1],
          ['match', ['get', 'kind'], 'fire', 0.32, 'brwn', 0.3, 'tornado', 0.2, 'hurricane', 0.26, 0.26],
          0.12,
        ],
      },
    })
    map.addLayer({
      id: 'sc-zones-line',
      type: 'line',
      source: SRC_ZONES,
      filter: ['all', ['!=', ['get', 'kind'], 'tornado-dir'], ['!=', ['get', 'kind'], 'hurricane-dir']],
      paint: {
        'line-color': [
          'match',
          ['get', 'kind'],
          'fire',
          '#FF4D00',
          'brwn',
          '#A0522D',
          'tornado',
          '#C9A6FF',
          'hurricane',
          '#8FB4E8',
          '#FF8C1A',
        ],
        'line-width': ['case', ['==', ['get', 'selected'], 1], 4.5, ['==', ['get', 'lit'], 1], 2.5, 1.75],
        'line-dasharray': [2, 2],
        'line-opacity': ['case', ['==', ['get', 'selected'], 1], 1, 0.55],
      },
    })
  }
  if (!map.getSource(SRC_LINES)) {
    map.addSource(SRC_LINES, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  }
  // Dashed intended routes under solid what-if tracks.
  if (map.getSource(SRC_LINES) && !map.getLayer('sc-lines-dash')) {
    map.addLayer({
      id: 'sc-lines-dash',
      type: 'line',
      source: SRC_LINES,
      filter: ['==', ['get', 'dash'], 1],
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#8a9bb0'],
        'line-width': ['coalesce', ['get', 'width'], 3],
        'line-opacity': ['coalesce', ['get', 'opacity'], 0.55],
        'line-dasharray': [2.4, 1.8],
      },
    })
  }
  if (map.getSource(SRC_LINES) && !map.getLayer('sc-lines')) {
    map.addLayer({
      id: 'sc-lines',
      type: 'line',
      source: SRC_LINES,
      filter: ['!=', ['get', 'dash'], 1],
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#FAC775'],
        'line-width': ['coalesce', ['get', 'width'], 5],
        'line-opacity': ['coalesce', ['get', 'opacity'], 0.85],
      },
    })
  } else if (map.getLayer('sc-lines')) {
    try {
      map.setFilter('sc-lines', ['!=', ['get', 'dash'], 1])
    } catch {
      /* layer may already be filtered */
    }
  }
  if (map.getSource(SRC_LINES) && !map.getLayer('sc-lines-hit')) {
    // Invisible fat stroke for reliable click/hit testing of thin board lines.
    map.addLayer({
      id: 'sc-lines-hit',
      type: 'line',
      source: SRC_LINES,
      paint: {
        'line-color': '#000000',
        'line-width': 22,
        'line-opacity': 0,
      },
    })
  }
  if (!map.getSource(SRC_FOCUS)) {
    map.addSource(SRC_FOCUS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-focus-ring',
      type: 'circle',
      source: SRC_FOCUS,
      paint: {
        'circle-radius': 1,
        'circle-color': 'transparent',
        'circle-stroke-width': 0,
        'circle-stroke-color': 'transparent',
        'circle-stroke-opacity': 0,
      },
    })
  }
  if (!map.getSource(SRC_HL)) {
    map.addSource(SRC_HL, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-pipe-hl',
      type: 'line',
      source: SRC_HL,
      paint: { 'line-color': '#5EEAD4', 'line-width': 5, 'line-opacity': 0.5 },
    })
  }
  // Hover emphasis on top of the cyan cluster — same family, slightly lighter.
  if (!map.getSource(SRC_HL_HOT)) {
    map.addSource(SRC_HL_HOT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-pipe-hl-hot',
      type: 'line',
      source: SRC_HL_HOT,
      paint: { 'line-color': '#8EF0E0', 'line-width': 7, 'line-opacity': 0.5 },
    })
  }
  if (!map.getSource(SRC_DRAW)) {
    map.addSource(SRC_DRAW, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-draw',
      type: 'line',
      source: SRC_DRAW,
      paint: { 'line-color': '#E24B4A', 'line-width': 4, 'line-opacity': 0.9 },
    })
  }
  if (!map.getSource(SRC_SCRIBBLE)) {
    map.addSource(SRC_SCRIBBLE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'sc-scribble',
      type: 'line',
      source: SRC_SCRIBBLE,
      paint: {
        'line-color': '#E24B4A',
        'line-width': 5.5,
        'line-opacity': 0.92,
        'line-blur': 0.35,
      },
    })
  }
  if (!map.getSource(SRC_KF)) {
    map.addSource(SRC_KF, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  }
  if (map.getSource(SRC_KF) && !map.getLayer('sc-kf-path')) {
    map.addLayer({
      id: 'sc-kf-path',
      type: 'line',
      source: SRC_KF,
      filter: ['==', ['get', 'kind'], 'path'],
      paint: {
        'line-color': ['case', ['==', ['get', 'selected'], 1], '#5EB0FF', '#8BB8D9'],
        'line-width': ['case', ['==', ['get', 'selected'], 1], 3.5, 2],
        'line-dasharray': [1.2, 1.2],
        'line-opacity': ['case', ['==', ['get', 'selected'], 1], 0.95, 0.7],
      },
    })
  }
  if (map.getSource(SRC_KF) && !map.getLayer('sc-kf-point')) {
    map.addLayer({
      id: 'sc-kf-point',
      type: 'circle',
      source: SRC_KF,
      filter: ['==', ['get', 'kind'], 'point'],
      layout: {
        'circle-sort-key': ['get', 'z'],
      },
      paint: {
        'circle-radius': ['case', ['==', ['get', 'selected'], 1], 11, 9],
        'circle-color': ['case', ['==', ['get', 'selected'], 1], '#1D6FA5', '#FFFFFF'],
        'circle-stroke-width': 2,
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'selected'], 1],
          '#7EC8FF',
          ['==', ['get', 'dwell'], 1],
          '#E8B048',
          '#1D6FA5',
        ],
        // Slight transparency so map labels (e.g. road names) remain readable under pins.
        'circle-opacity': ['case', ['==', ['get', 'selected'], 1], 0.92, 0.72],
        'circle-stroke-opacity': ['case', ['==', ['get', 'selected'], 1], 0.95, 0.8],
      },
    })
  } else if (map.getLayer('sc-kf-point')) {
    map.setLayoutProperty('sc-kf-point', 'circle-sort-key', ['get', 'z'])
    map.setPaintProperty('sc-kf-point', 'circle-color', [
      'case',
      ['==', ['get', 'selected'], 1],
      '#1D6FA5',
      '#FFFFFF',
    ])
    map.setPaintProperty('sc-kf-point', 'circle-stroke-color', [
      'case',
      ['==', ['get', 'selected'], 1],
      '#7EC8FF',
      ['==', ['get', 'dwell'], 1],
      '#E8B048',
      '#1D6FA5',
    ])
    map.setPaintProperty('sc-kf-point', 'circle-opacity', [
      'case',
      ['==', ['get', 'selected'], 1],
      0.92,
      0.72,
    ])
    map.setPaintProperty('sc-kf-point', 'circle-stroke-opacity', [
      'case',
      ['==', ['get', 'selected'], 1],
      0.95,
      0.8,
    ])
  }
  if (map.getLayer('sc-kf-label')) {
    try {
      map.setLayoutProperty('sc-kf-label', 'text-field', ['to-string', ['get', 'label']])
    } catch {
      /* style not ready */
    }
  }
  if (map.getSource(SRC_KF) && !map.getLayer('sc-kf-hit')) {
    map.addLayer({
      id: 'sc-kf-hit',
      type: 'circle',
      source: SRC_KF,
      filter: ['==', ['get', 'kind'], 'point'],
      paint: {
        'circle-radius': 16,
        'circle-opacity': 0,
      },
    })
  }
  if (map.getSource(SRC_KF) && !map.getLayer('sc-kf-label')) {
    try {
      map.addLayer({
        id: 'sc-kf-label',
        type: 'symbol',
        source: SRC_KF,
        filter: ['==', ['get', 'kind'], 'point'],
        layout: {
          'text-field': ['to-string', ['get', 'label']],
          'text-size': 11,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-anchor': 'center',
          'symbol-sort-key': ['get', 'z'],
        },
        paint: {
          'text-color': ['case', ['==', ['get', 'selected'], 1], '#FFFFFF', '#0B1F2E'],
          'text-halo-color': [
            'case',
            ['==', ['get', 'selected'], 1],
            'rgba(0, 0, 0, 0)',
            'rgba(255, 255, 255, 0.55)',
          ],
          'text-halo-width': ['case', ['==', ['get', 'selected'], 1], 0, 0.8],
          'text-opacity': 1,
        },
      })
    } catch {
      /* Some custom styles lack glyph fonts; circles/path still work. */
    }
  } else if (map.getLayer('sc-kf-label')) {
    try {
      map.setLayoutProperty('sc-kf-label', 'symbol-sort-key', ['get', 'z'])
      map.setPaintProperty('sc-kf-label', 'text-color', [
        'case',
        ['==', ['get', 'selected'], 1],
        '#FFFFFF',
        '#0B1F2E',
      ])
      map.setPaintProperty('sc-kf-label', 'text-halo-color', [
        'case',
        ['==', ['get', 'selected'], 1],
        'rgba(0, 0, 0, 0)',
        'rgba(255, 255, 255, 0.55)',
      ])
      map.setPaintProperty('sc-kf-label', 'text-halo-width', [
        'case',
        ['==', ['get', 'selected'], 1],
        0,
        0.8,
      ])
      map.setPaintProperty('sc-kf-label', 'text-opacity', 1)
    } catch {
      /* ignore */
    }
  }
}

function setGeo(map: mapboxgl.Map, sourceId: string, data: FeatureCollection): void {
  const src = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined
  src?.setData(data)
}

/** Shift a plume polygon with the marker in the same drag event, before React commits. */
function shiftPlumeGeometry(geometry: Geometry, dLng: number, dLat: number): Geometry {
  if (geometry.type !== 'Polygon') return geometry
  return {
    type: 'Polygon',
    coordinates: geometry.coordinates.map((ring) =>
      ring.map((pt) => [pt[0] + dLng, pt[1] + dLat]),
    ),
  }
}

/**
 * Drag used to rewrite every plume from the pointer-down snapshot. That kept the
 * dragged wedge glued to the cursor, and also painted everyone else back to the
 * wind they had when the drag started — so a leak the tornado ring had just
 * covered flickered between Surface wind and tornado wind on every move.
 * Shift only the dragged object's own wedges. Leave the rest on the latest paint.
 */
function plumesFollowingDrag(
  base: FeatureCollection,
  latest: FeatureCollection,
  dragId: string,
  dLng: number,
  dLat: number,
): FeatureCollection {
  const owned = base.features.flatMap((feature) => {
    if (feature.properties?.id !== dragId || !feature.geometry) return []
    return [{ ...feature, geometry: shiftPlumeGeometry(feature.geometry, dLng, dLat) }]
  })
  const rest = latest.features.filter((feature) => feature.properties?.id !== dragId)
  return { type: 'FeatureCollection', features: [...rest, ...owned] }
}

/** Layers that stay full-bright during inspect focus-dim (everything else is dimmed). */
const BASEMAP_DIM_ID = 'sc-basemap-dim'

const FOCUS_DIM_KEEP = new Set([
  BASEMAP_DIM_ID,
  'sc-pipe-hl',
  'sc-pipe-hl-hot',
  // Waypoint / Position path must stay readable while the basemap dims.
  'sc-kf-path',
  'sc-kf-point',
  'sc-kf-label',
  'sc-plumes-fill',
  'sc-plumes-line',
])

type FocusDimPaintProp = Parameters<mapboxgl.Map['setPaintProperty']>[1]
type FocusDimBackup = { layerId: string; prop: FocusDimPaintProp; value: unknown }

const FOCUS_DIM_PROPS: Record<string, readonly FocusDimPaintProp[]> = {
  background: ['background-opacity'],
  fill: ['fill-opacity'],
  line: ['line-opacity'],
  symbol: ['icon-opacity', 'text-opacity'],
  raster: ['raster-opacity'],
  circle: ['circle-opacity', 'circle-stroke-opacity'],
  heatmap: ['heatmap-opacity'],
  'fill-extrusion': ['fill-extrusion-opacity'],
}

const FOCUS_DIM_TARGET: Partial<Record<FocusDimPaintProp, number>> = {
  'background-opacity': 0.45,
  'fill-opacity': 0.1,
  'line-opacity': 0.16,
  'icon-opacity': 0.18,
  'text-opacity': 0.18,
  'raster-opacity': 0.36,
  'circle-opacity': 0.12,
  'circle-stroke-opacity': 0.12,
  'heatmap-opacity': 0.18,
  'fill-extrusion-opacity': 0.12,
}

/**
 * Satellite basemap lives in a style import, not in getStyle().layers, so opacity
 * paint never reaches it. A fill under our overlays darkens that imagery and
 * leaves the width ring, which is drawn above this fill, bright.
 */
/** Viewport-sized cover. A world polygon does not paint on the globe basemap. */
function basemapVeilData(map: mapboxgl.Map): Feature<Polygon> {
  const bounds = map.getBounds()
  let west: number
  let east: number
  let south: number
  let north: number
  if (
    bounds &&
    bounds.getEast() > bounds.getWest() &&
    bounds.getNorth() > bounds.getSouth()
  ) {
    const padLng = Math.max(0.08, (bounds.getEast() - bounds.getWest()) * 0.4)
    const padLat = Math.max(0.08, (bounds.getNorth() - bounds.getSouth()) * 0.4)
    west = bounds.getWest() - padLng
    east = bounds.getEast() + padLng
    south = Math.max(-85, bounds.getSouth() - padLat)
    north = Math.min(85, bounds.getNorth() + padLat)
  } else {
    const center = map.getCenter()
    west = center.lng - 2
    east = center.lng + 2
    south = Math.max(-85, center.lat - 2)
    north = Math.min(85, center.lat + 2)
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south],
        ],
      ],
    },
  }
}

function syncBasemapVeil(map: mapboxgl.Map, enabled: boolean): void {
  try {
    const exists = Boolean(map.getLayer(BASEMAP_DIM_ID))
    if (!enabled) {
      if (exists) map.removeLayer(BASEMAP_DIM_ID)
      if (map.getSource(BASEMAP_DIM_ID)) map.removeSource(BASEMAP_DIM_ID)
      return
    }
    const data = basemapVeilData(map)
    const source = map.getSource(BASEMAP_DIM_ID) as mapboxgl.GeoJSONSource | undefined
    if (source) {
      source.setData(data)
      if (!exists) {
        const beforeId = ['sc-plumes-fill', 'sc-zones-fill', 'sc-lines'].find((id) =>
          map.getLayer(id),
        )
        map.addLayer(
          {
            id: BASEMAP_DIM_ID,
            type: 'fill',
            source: BASEMAP_DIM_ID,
            paint: { 'fill-color': '#05050a', 'fill-opacity': 0.5 },
          },
          beforeId,
        )
      }
      return
    }
    map.addSource(BASEMAP_DIM_ID, { type: 'geojson', data })
    const beforeId = ['sc-plumes-fill', 'sc-zones-fill', 'sc-lines'].find((id) => map.getLayer(id))
    map.addLayer(
      {
        id: BASEMAP_DIM_ID,
        type: 'fill',
        source: BASEMAP_DIM_ID,
        paint: { 'fill-color': '#05050a', 'fill-opacity': 0.5 },
      },
      beforeId,
    )
  } catch {
    /* style mid-reload */
  }
}

function restoreFocusDimPaint(map: mapboxgl.Map, backup: FocusDimBackup[]): void {
  for (const entry of backup) {
    if (!map.getLayer(entry.layerId)) continue
    try {
      map.setPaintProperty(entry.layerId, entry.prop, entry.value ?? 1)
    } catch {
      /* layer/paint may have changed with style */
    }
  }
}

/** Dim every style layer by default; keep only FOCUS_DIM_KEEP bright. */
function applyFocusDimPaint(
  map: mapboxgl.Map,
  enabled: boolean,
  backup: FocusDimBackup[],
  extraKeep: readonly string[] = [],
): FocusDimBackup[] {
  restoreFocusDimPaint(map, backup)
  if (!enabled) return []

  const next: FocusDimBackup[] = []
  const layers = map.getStyle()?.layers ?? []
  for (const layer of layers) {
    if (FOCUS_DIM_KEEP.has(layer.id) || extraKeep.includes(layer.id)) continue
    // Invisible hit targets — leave alone.
    if (layer.id.endsWith('-hit')) continue
    const props = FOCUS_DIM_PROPS[layer.type]
    if (!props) continue
    for (const prop of props) {
      try {
        const prev = map.getPaintProperty(layer.id, prop)
        next.push({ layerId: layer.id, prop, value: prev })
        map.setPaintProperty(layer.id, prop, FOCUS_DIM_TARGET[prop] ?? 0.2)
      } catch {
        /* paint prop unavailable on this layer */
      }
    }
  }
  if (map.getLayer('sc-pipe-hl')) {
    try {
      map.moveLayer('sc-pipe-hl')
      map.setPaintProperty('sc-pipe-hl', 'line-color', '#5EEAD4')
      map.setPaintProperty('sc-pipe-hl', 'line-width', 6.5)
      map.setPaintProperty('sc-pipe-hl', 'line-opacity', 0.95)
    } catch {
      /* ignore */
    }
  }
  if (map.getLayer('sc-pipe-hl-hot')) {
    try {
      map.moveLayer('sc-pipe-hl-hot')
      map.setPaintProperty('sc-pipe-hl-hot', 'line-color', '#8EF0E0')
      map.setPaintProperty('sc-pipe-hl-hot', 'line-width', 8)
      map.setPaintProperty('sc-pipe-hl-hot', 'line-opacity', 0.55)
    } catch {
      /* ignore */
    }
  }
  return next
}

function distPointToSegPx(
  px: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 <= 1e-6) return Math.hypot(px.x - a.x, px.y - a.y)
  let t = ((px.x - a.x) * dx + (px.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px.x - (a.x + t * dx), px.y - (a.y + t * dy))
}

/** Nearest board line object within maxPx of a canvas point (screen space). */
function nearestBoardLineId(
  map: mapboxgl.Map,
  point: { x: number; y: number },
  objects: readonly ResolvedObject[],
  maxPx = 16,
  includeGhosts = false,
): string | null {
  let bestId: string | null = null
  let bestDist = maxPx
  for (const obj of objects) {
    if (obj.ghost && !includeGhosts) continue
    const segs = objectLineSegments(obj)
    if (!segs.length) continue
    for (const seg of segs) {
      for (let i = 1; i < seg.length; i++) {
        const a = map.project(seg[i - 1]!)
        const b = map.project(seg[i]!)
        const d = distPointToSegPx(point, a, b)
        if (d < bestDist) {
          bestDist = d
          bestId = obj.id
        }
      }
    }
  }
  return bestId
}

/** True when a zone or plume fill is under the cursor. Those popups own the tip. */
function overlayFillAt(map: mapboxgl.Map, point: { x: number; y: number }): boolean {
  const layers = ['sc-zones-fill', 'sc-plumes-fill'].filter((id) => {
    try {
      return Boolean(map.getLayer(id))
    } catch {
      return false
    }
  })
  if (!layers.length) return false
  try {
    return map.queryRenderedFeatures([point.x, point.y], { layers }).length > 0
  } catch {
    return false
  }
}

function focusFeatures(objects: readonly ResolvedObject[], ids: readonly string[]): FeatureCollection {
  const want = new Set(ids)
  const features: FeatureCollection['features'] = []
  for (const obj of objects) {
    if (!want.has(obj.id) || !obj.pose) continue
    features.push({
      type: 'Feature',
      properties: { id: obj.id },
      geometry: { type: 'Point', coordinates: [obj.pose.lng, obj.pose.lat] },
    })
  }
  return { type: 'FeatureCollection', features }
}

/** Zone and plume tips register here so an icon under the cursor can dismiss them. */
const underlayTipHiders = new Set<() => void>()

function originKey(pose: { lng: number; lat: number }): string {
  return `${pose.lng.toFixed(6)},${pose.lat.toFixed(6)}`
}

function hoverTitleForStack(active: ResolvedObject, stack: readonly ResolvedObject[]): string {
  const labelOf = (o: ResolvedObject) => o.mapLabel?.trim() || o.label.trim() || paletteLabel(o.type)
  const activeLabel = labelOf(active)
  const rest = stack
    .filter((o) => o.id !== active.id)
    .map(labelOf)
    .filter((t) => t.length > 0 && t !== activeLabel)
  const uniq = [...new Set(rest)]
  return [activeLabel, ...uniq].filter(Boolean).join(' · ')
}

function appearRank(obj: ResolvedObject): number {
  const iso = obj.startsAt ?? obj.addedAt
  if (!iso) return 0
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}

/** Prefer vehicles/people pins over zone footprints when co-located. */
function stackTypeRank(obj: ResolvedObject): number {
  if (VEHICLE_TYPES.has(obj.type)) return 3
  if (HAZARD_TYPES.has(obj.type)) return 1
  return 2
}

function pickStackActive(
  stack: readonly ResolvedObject[],
  selectedId: string | null,
  highlightIds: readonly string[],
): ResolvedObject {
  if (selectedId) {
    const hit = stack.find((o) => o.id === selectedId)
    if (hit) return hit
  }
  for (const id of highlightIds) {
    const hit = stack.find((o) => o.id === id)
    if (hit) return hit
  }
  const live = stack.filter((o) => !o.ghost)
  const pool = live.length ? live : stack
  return pool.reduce((best, o) => {
    const typeDelta = stackTypeRank(o) - stackTypeRank(best)
    if (typeDelta !== 0) return typeDelta > 0 ? o : best
    return appearRank(o) >= appearRank(best) ? o : best
  })
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Free-text labels: author size is a screen weight at z≈15, with a soft zoom nudge
 * clamped to ~±20% so text adapts to the scene without billboard drift.
 */
function labelFontPx(size: ObjectSize | string | undefined, zoom: number): number {
  const key = (size === 'sm' || size === 'md' || size === 'lg' || size === 'xl' ? size : 'md') as ObjectSize
  const base = TYPE_SIZE_PX[key]
  const nudged = base * (1 + 0.06 * (zoom - 15))
  const lo = base * 0.8
  const hi = base * 1.2
  return Math.max(lo, Math.min(hi, nudged))
}

function shouldShowMapCaption(
  mapZoom: number,
  selected: boolean,
  lit: boolean,
): boolean {
  if (selected || lit) return true
  return mapZoom >= CAPTION_MIN_ZOOM
}

function paintStackMarker(
  inner: HTMLElement,
  active: ResolvedObject,
  stack: readonly ResolvedObject[],
  selected: boolean,
  lit: boolean,
  title: string,
  mapZoom = 15,
  linkHl = false,
  ghostFlash = false,
  remote = false,
): void {
  const variant = iconVariantFor(active)
  inner.dataset.type = active.type
  inner.dataset.id = active.id
  const hazard = HAZARD_TYPES.has(active.type)
  const vehicle = VEHICLE_TYPES.has(active.type)
  const asText = active.type === 'label' || (active.type === 'sketch' && objectLineSegments(active).length === 0)
  const pathSketchGrip = active.type === 'sketch' && objectLineSegments(active).length > 0
  const stacked = stack.length > 1
  // Authored mapLabel captions stay visible at any zoom (pause/play must match).
  const hasAuthoredMapLabel = stack.some((o) => Boolean(o.mapLabel?.trim()))
  const showCaps =
    hasAuthoredMapLabel || shouldShowMapCaption(mapZoom, selected, lit || ghostFlash)
  const caps = showCaps
    ? stack
        .map((o) => {
          const text = o.mapLabel?.trim() || (stacked ? o.label : '')
          return { id: o.id, text, active: o.id === active.id }
        })
        .filter((c) => c.text.length > 0)
    : []
  caps.sort((a, b) => Number(b.active) - Number(a.active) || a.text.localeCompare(b.text))
  const hasCap = caps.length > 0
  const tip = hoverTitleForStack(active, stack)
  inner.style.display = ''
  inner.className = [
    'sc-mark',
    selected ? 'sel' : '',
    !selected && remote ? 'remote' : '',
    selected && linkHl ? 'link-hl' : '',
    active.ghost ? 'ghost' : '',
    ghostFlash ? 'ghost-flash' : '',
    lit ? 'lit' : '',
    hazard ? 'hazard' : '',
    vehicle ? 'vehicle' : '',
    hasCap ? 'has-cap' : '',
    stacked ? 'stacked' : '',
    asText ? 'label' : '',
    pathSketchGrip ? 'sketch-grip' : '',
    `size-${active.size}`,
  ]
    .filter(Boolean)
    .join(' ')
  if (pathSketchGrip) {
    inner.style.color = ''
    inner.style.fontSize = ''
    inner.innerHTML = ''
    setMarkTip(inner, tip || title || 'Drag to move')
    return
  }
  if (asText && !stacked) {
    inner.style.color =
      active.type === 'label' ? String(active.params.color ?? '#ffffff') : String(active.params.color ?? '#82C4F8')
    inner.style.fontSize = `${labelFontPx(active.size, mapZoom)}px`
    inner.textContent =
      active.type === 'label' ? String(active.params.text ?? active.label) : active.label
    setMarkTip(inner, tip || title)
    return
  }
  // Optional params.color override (e.g. airplane turns red after bird strike).
  const paramColor =
    typeof active.params.color === 'string' ? active.params.color.trim() : ''
  inner.style.color = paramColor || paletteColor(active.type)
  inner.style.fontSize = ''
  if (hasCap) {
    const capsHtml = caps
      .map(
        (c) =>
          `<span class="sc-mark-cap${c.active ? ' on' : ' dim'}">${escapeHtml(c.text)}</span>`,
      )
      .join('')
    inner.innerHTML = `<span class="sc-mark-face">${iconSvg(active.type, variant, 32)}</span><span class="sc-mark-caps">${capsHtml}</span>`
  } else {
    inner.innerHTML = iconSvg(active.type, variant, 32)
  }
  setMarkTip(inner, tip || title)
}

function setMarkTip(inner: HTMLElement, text: string): void {
  inner.removeAttribute('title')
  if (text) inner.dataset.tip = text
  else delete inner.dataset.tip
}

type MarkerPaintArgs = {
  active: ResolvedObject
  stack: readonly ResolvedObject[]
  selected: boolean
  lit: boolean
  title: string
  linkHl: boolean
  ghostFlash: boolean
  remote: boolean
}

export const ScenarioMapHost = memo(function ScenarioMapHost({
  styleUrl,
  accessToken,
  camera,
  directedCamera = null,
  onUserCameraGesture,
  onCameraFrame,
  watchCursors = [],
  watchFocusId = null,
  remoteDragId = null,
  objects,
  selectedId,
  highlightIds,
  iconHighlightIds = [],
  viewResetId,
  placeArmed,
  freehandArmed,
  penArmed = false,
  rulerArmed = false,
  scribbleArmed = false,
  scribbleVisible,
  scribbleStrokes = [],
  sketchStyle,
  editing,
  canDragObject,
  waypointObjectId = null,
  plumeFocusObjectId = null,
  hideWaypointUnitIcon = false,
  waypointFocusIndex = null,
  waypointsEditable = false,
  onWaypointFocus,
  onWaypointMove,
  pathHoldLabel = null,
  onSelectObject,
  onPlace,
  onStroke,
  onScribbleStroke,
  onRulerChange,
  onMoveObject,
  onPipelineTap,
  onBasemapPipelines,
  mapAssetChip = null,
  onMapAssetDetails,
  onMapAssetDismiss,
  mapFeatureHighlight = null,
  attachedHighlightObjects = [],
  linkHighlightSelected = false,
  focusDim = false,
  focusDimKeep = [],
  onPlumeHover,
  ghostFlashIds = [],
  iconsOnly = false,
  surfacePlumes = true,
  mapApiRef,
  onMapIdle,
  onObjectMoveEnd,
  cameraFollowOn = false,
  onCameraFollowChange,
  showAutoTrack = false,
  samplePlayheadIso,
}: ScenarioMapHostProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const assetChipRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const autoTrackBtnRef = useRef<HTMLButtonElement | null>(null)
  const cameraFollowOnRef = useRef(cameraFollowOn)
  cameraFollowOnRef.current = cameraFollowOn
  const showAutoTrackRef = useRef(showAutoTrack)
  showAutoTrackRef.current = showAutoTrack
  const onCameraFollowChangeRef = useRef(onCameraFollowChange)
  onCameraFollowChangeRef.current = onCameraFollowChange
  const watchCursorMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map())
  const watchTipRef = useRef<mapboxgl.Popup | null>(null)
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map())
  const markerPaintRef = useRef<Map<string, MarkerPaintArgs>>(new Map())
  const styleReadyRef = useRef(false)
  /** Hide the previous basemap while a new style loads (avoids Eagle Mountain flash). */
  const [stylePaintReady, setStylePaintReady] = useState(false)
  const [styleEpoch, setStyleEpoch] = useState(0)
  /** Style URL the live map was constructed with, or last passed to setStyle. */
  const createdStyleUrlRef = useRef('')
  const onMapIdleRef = useRef(onMapIdle)
  onMapIdleRef.current = onMapIdle
  const onObjectMoveEndRef = useRef(onObjectMoveEnd)
  onObjectMoveEndRef.current = onObjectMoveEnd
  const linkHighlightSelectedRef = useRef(linkHighlightSelected)
  linkHighlightSelectedRef.current = linkHighlightSelected
  const focusDimBackupRef = useRef<FocusDimBackup[]>([])
  const focusDimRef = useRef(focusDim)
  focusDimRef.current = focusDim
  const focusDimKeepRef = useRef(focusDimKeep)
  focusDimKeepRef.current = focusDimKeep
  const [coordToast, setCoordToast] = useState<string | null>(null)
  const placeArmedRef = useRef(placeArmed)
  placeArmedRef.current = placeArmed
  const freehandRef = useRef(freehandArmed)
  freehandRef.current = freehandArmed
  const penRef = useRef(penArmed)
  penRef.current = penArmed
  const rulerRef = useRef(rulerArmed)
  rulerRef.current = rulerArmed
  const scribbleRef = useRef(scribbleArmed)
  scribbleRef.current = scribbleArmed
  const scribbleDraftRef = useRef<[number, number][]>([])
  const sketchStyleRef = useRef(sketchStyle)
  sketchStyleRef.current = sketchStyle
  const editingRef = useRef(editing)
  editingRef.current = editing
  const canDragObjectRef = useRef(canDragObject)
  canDragObjectRef.current = canDragObject
  const onSelectRef = useRef(onSelectObject)
  onSelectRef.current = onSelectObject
  const onPlaceRef = useRef(onPlace)
  onPlaceRef.current = onPlace
  const onStrokeRef = useRef(onStroke)
  onStrokeRef.current = onStroke
  const onScribbleStrokeRef = useRef(onScribbleStroke)
  onScribbleStrokeRef.current = onScribbleStroke
  const onRulerChangeRef = useRef(onRulerChange)
  onRulerChangeRef.current = onRulerChange
  const onMoveRef = useRef(onMoveObject)
  onMoveRef.current = onMoveObject
  const onPipeRef = useRef(onPipelineTap)
  onPipeRef.current = onPipelineTap
  const onBasemapPipelinesRef = useRef(onBasemapPipelines)
  onBasemapPipelinesRef.current = onBasemapPipelines
  const onWaypointFocusRef = useRef(onWaypointFocus)
  onWaypointFocusRef.current = onWaypointFocus
  const onWaypointMoveRef = useRef(onWaypointMove)
  onWaypointMoveRef.current = onWaypointMove
  const waypointObjectIdRef = useRef(waypointObjectId)
  waypointObjectIdRef.current = waypointObjectId
  const draggingIdsRef = useRef(new Set<string>())
  const dragFromRef = useRef(new Map<string, { lng: number; lat: number }>())
  /** Last plume collection written from React. Drag shifts a snapshot of this, not the live source. */
  const plumeFcRef = useRef<FeatureCollection>(EMPTY_FC)
  /** Last zone collection written from React. A footprint drag shifts a snapshot of this. */
  const zoneFcRef = useRef<FeatureCollection>(EMPTY_FC)
  /** Moves the open plume or zone tip with a drag. Map mousemove does not fire during that gesture. */
  const followOverlayTipRef = useRef<((lng: number, lat: number) => void) | null>(null)
  const plumeDragRef = useRef<{
    id: string
    lng: number
    lat: number
    /** Current point. The footprint and icon both read this so React cannot paint a stale ring. */
    liveLng: number
    liveLat: number
    base: FeatureCollection
  } | null>(null)
  const draggingWaypointRef = useRef<number | null>(null)
  const draftPathRef = useRef<[number, number][]>([])
  const penDraftRef = useRef<[number, number][]>([])
  const rulerDraftRef = useRef<[number, number][]>([])
  const objectsRef = useRef(objects)
  objectsRef.current = objects
  const samplePlayheadIsoRef = useRef(samplePlayheadIso)
  samplePlayheadIsoRef.current = samplePlayheadIso
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const watchPointerRef = useRef<{
    px: { x: number; y: number } | null
    down: boolean
    clickSeq: number
    hoverId?: string
    hoverTip?: string
    grab: boolean
    cursor: 'pointer' | 'grab' | 'grabbing'
  }>({ px: null, down: false, clickSeq: 0, grab: false, cursor: 'grab' })
  const highlightIdsRef = useRef(highlightIds)
  highlightIdsRef.current = highlightIds
  /** While a line/sketch is being dragged, the overlay effect must not reset its geometry. */
  const strokeDragRef = useRef(false)
  const suppressLineClickRef = useRef(false)
  const suppressLineClickTimerRef = useRef<number | null>(null)
  /**
   * Swallow the map click that belongs to this press, then drop the flag.
   * preventDefault on pointerdown often cancels that click, and a flag left
   * set made the next press (place, or click-away to Settings) do nothing.
   */
  const armSuppressLineClickRef = useRef<() => void>(() => {})
  armSuppressLineClickRef.current = () => {
    suppressLineClickRef.current = true
    if (suppressLineClickTimerRef.current != null) {
      window.clearTimeout(suppressLineClickTimerRef.current)
    }
    suppressLineClickTimerRef.current = window.setTimeout(() => {
      suppressLineClickTimerRef.current = null
      suppressLineClickRef.current = false
    }, 50)
  }
  const coordCopyTimerRef = useRef<number | null>(null)
  const onUserCameraGestureRef = useRef(onUserCameraGesture)
  onUserCameraGestureRef.current = onUserCameraGesture
  const onCameraFrameRef = useRef(onCameraFrame)
  onCameraFrameRef.current = onCameraFrame
  /** Throttle Watch emits from object onDrag (same handler that moves the marker). */
  const dragWatchEmitAtRef = useRef(0)
  /** Ignore Mapbox move events fired by our own easeTo/jumpTo. */
  const programmaticMoveRef = useRef(false)
  /** Latest director beat — read in foundation effect without re-running when follow toggles off. */
  const directedCameraRef = useRef(directedCamera)
  directedCameraRef.current = directedCamera
  const hadDirectedCameraRef = useRef(false)
  /** Live single-object drag pose for getCamera / Share flush (matches onCameraFrame). */
  const getLiveDragPose = (): {
    dragId?: string
    dragLng?: number
    dragLat?: number
  } | null => {
    const dragId =
      draggingIdsRef.current.size === 1 ? [...draggingIdsRef.current][0] : undefined
    if (!dragId) return null
    const dragMarker = markersRef.current.get(`id:${dragId}`)?.getLngLat()
    const dragLive =
      plumeDragRef.current?.id === dragId ? plumeDragRef.current : null
    const dragLng = dragMarker?.lng ?? dragLive?.liveLng
    const dragLat = dragMarker?.lat ?? dragLive?.liveLat
    if (dragLng == null || dragLat == null) return null
    return { dragId, dragLng, dragLat }
  }

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    mapboxgl.accessToken = accessToken
    const style = styleUrl
    createdStyleUrlRef.current = styleUrl
    const map = new mapboxgl.Map({
      container: el,
      style,
      center: [camera.lng, camera.lat],
      zoom: camera.zoom,
      attributionControl: SHOW_MAP_ATTRIBUTION,
      // Overlays are top-down only. Ctrl/right-drag and trackpad tilt are Mapbox defaults.
      pitch: 0,
      bearing: 0,
      maxPitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    })
    // Compass/pitch off for now — 2D boards don't support pitch well; frees the
    // bottom slot in this group for Auto-track.
    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false,
      }),
      'bottom-right',
    )
    const detachAutoTrack = attachAutoTrackControl(map, {
      getOn: () => cameraFollowOnRef.current,
      getVisible: () => showAutoTrackRef.current,
      onToggle: () => {
        const next = !cameraFollowOnRef.current
        onCameraFollowChangeRef.current?.(next)
      },
      buttonRef: autoTrackBtnRef,
    })
    // Shift is used for copy-coords; Mapbox box-zoom would steal Shift+drag.
    map.boxZoom.disable()
    // Arrow keys seek story beats in Studio; Mapbox pan-by-keyboard fights that.
    map.keyboard.disable()
    map.dragRotate.disable()
    map.touchPitch.disable()
    map.touchZoomRotate.disableRotation()
    mapRef.current = map

    // Shift+click copies coords; Shift+hover shows a pointer (Mapbox defaults to grab).
    // Keep this on map click/mousemove — do not capture mousedown (that broke copy).
    let shiftHeld = false
    const canvas = map.getCanvas()
    const container = map.getCanvasContainer()

    const copyCursorLatLng = (lat: number, lng: number): void => {
      const text = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      const show = (msg: string): void => {
        setCoordToast(msg)
        if (coordCopyTimerRef.current != null) window.clearTimeout(coordCopyTimerRef.current)
        coordCopyTimerRef.current = window.setTimeout(() => setCoordToast(null), 1800)
      }
      const fallbackCopy = (): boolean => {
        try {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          const ok = document.execCommand('copy')
          document.body.removeChild(ta)
          return ok
        } catch {
          return false
        }
      }
      // Show feedback immediately so a slow/denied clipboard still feels responsive.
      show(`Copied ${text}`)
      void (async () => {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
          }
        } catch {
          /* fall through */
        }
        if (!fallbackCopy()) show('Copy failed')
      })()
    }

    const syncCoordCursor = (on: boolean): void => {
      shiftHeld = on
      const wrap = wrapRef.current
      const shell = wrap?.parentElement
      wrap?.classList.toggle('coord-pick', on)
      shell?.classList.toggle('coord-pick', on)
      if (on) {
        // Crosshair — Mapbox / GIS convention for sampling an exact map point.
        canvas.style.setProperty('cursor', 'crosshair', 'important')
        container.style.setProperty('cursor', 'crosshair', 'important')
      } else {
        canvas.style.removeProperty('cursor')
        container.style.removeProperty('cursor')
      }
    }

    const onShiftKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Shift') return
      syncCoordCursor(e.type === 'keydown')
    }
    const onWindowBlur = (): void => syncCoordCursor(false)
    const onMapMouseMove = (ev: mapboxgl.MapMouseEvent): void => {
      const on = !!(ev.originalEvent as MouseEvent | undefined)?.shiftKey
      if (on !== shiftHeld) syncCoordCursor(on)
      else if (on) {
        canvas.style.setProperty('cursor', 'crosshair', 'important')
        container.style.setProperty('cursor', 'crosshair', 'important')
      }
    }

    window.addEventListener('keydown', onShiftKey)
    window.addEventListener('keyup', onShiftKey)
    window.addEventListener('blur', onWindowBlur)
    map.on('mousemove', onMapMouseMove)

    const onReady = (): void => {
      ensureOverlayLayers(map)
      rememberLineLayerIds(map)
      styleReadyRef.current = true
      // Don't wait only on idle — hub park / light styles can leave the veil up otherwise.
      setStylePaintReady(true)
      if (mapApiRef) {
        mapApiRef.current = bindMapApi(map, programmaticMoveRef, getLiveDragPose)
      }
      setStyleEpoch((n) => n + 1)
    }
    const onIdle = (): void => {
      if (styleReadyRef.current) setStylePaintReady(true)
      onMapIdleRef.current?.()
    }
    map.on('load', onReady)
    map.on('idle', onIdle)

    map.on('click', (ev) => {
      const assetPoint = { x: ev.point.x, y: ev.point.y }
      const clearAssetPeek = (): void => {
        applyHotHighlight(map, null)
        onBasemapPipelinesRef.current?.([], assetPoint)
      }
      if (suppressLineClickRef.current) {
        suppressLineClickRef.current = false
        clearAssetPeek()
        return
      }
      if (freehandRef.current || penRef.current || rulerRef.current || scribbleRef.current) {
        clearAssetPeek()
        return
      }

      const oe = ev.originalEvent as MouseEvent | undefined
      // Any role: Shift-click copies cursor lat,lng for chat / radio / notes.
      if (oe?.shiftKey) {
        oe.preventDefault()
        clearAssetPeek()
        copyCursorLatLng(ev.lngLat.lat, ev.lngLat.lng)
        return
      }

      // Placement never auto-attaches — drop at the cursor only.
      if (placeArmedRef.current) {
        clearAssetPeek()
        onPipeRef.current(null)
        onPlaceRef.current(ev.lngLat.lng, ev.lngLat.lat, null)
        return
      }

      // Prefer board lines (screen-space + fat hit layer). Asset peek stays off
      // this path — linking a pipe to an object is still Inspector-only.
      const fromGeom = nearestBoardLineId(map, ev.point, objectsRef.current, 16)
      if (fromGeom) {
        clearAssetPeek()
        onPipeRef.current(null)
        onSelectRef.current(fromGeom)
        return
      }
      if (map.getLayer('sc-lines-hit')) {
        try {
          const pad = 12
          const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
            [ev.point.x - pad, ev.point.y - pad],
            [ev.point.x + pad, ev.point.y + pad],
          ]
          const boardHits = map.queryRenderedFeatures(bbox, { layers: ['sc-lines-hit'] })
          const boardId = boardHits[0]?.properties?.id
          if (typeof boardId === 'string' && boardId) {
            clearAssetPeek()
            onPipeRef.current(null)
            onSelectRef.current(boardId)
            return
          }
        } catch {
          /* layer may be mid-style */
        }
      }

      // Waypoint pin clicks also fire this map-level handler; don't clear selection
      // (that jumps Inspect → Timeline). Layer handler already focused the row.
      if (waypointObjectIdRef.current) {
        const kfLayers = ['sc-kf-hit', 'sc-kf-point', 'sc-kf-label'].filter((id) =>
          map.getLayer(id),
        )
        if (kfLayers.length) {
          try {
            const pad = 4
            const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
              [ev.point.x - pad, ev.point.y - pad],
              [ev.point.x + pad, ev.point.y + pad],
            ]
            const kfHits = map.queryRenderedFeatures(bbox, { layers: kfLayers })
            if (kfHits.length) {
              clearAssetPeek()
              return
            }
          } catch {
            /* layer may be mid-style */
          }
        }
      }

      const pipes = queryPipelinesAtPoint(map, ev.point, ev.lngLat.lng, ev.lngLat.lat)
      applyHotHighlight(map, pipes[0] ?? null)
      onBasemapPipelinesRef.current?.(pipes, assetPoint)
      onPipeRef.current(null)
      onSelectRef.current(null)
    })

    if (mapApiRef) {
      mapApiRef.current = bindMapApi(map, programmaticMoveRef, getLiveDragPose)
    }

    return () => {
      detachAutoTrack()
      window.removeEventListener('keydown', onShiftKey)
      window.removeEventListener('keyup', onShiftKey)
      window.removeEventListener('blur', onWindowBlur)
      map.off('mousemove', onMapMouseMove)
      map.off('idle', onIdle)
      if (coordCopyTimerRef.current != null) window.clearTimeout(coordCopyTimerRef.current)
      if (mapApiRef) mapApiRef.current = null
      markersRef.current.forEach((m) => m.remove())
      markersRef.current.clear()
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep the Mapbox Auto-track button in sync with React state / keyboard T.
  useEffect(() => {
    const btn = autoTrackBtnRef.current
    if (!btn) return
    syncAutoTrackButton(btn, cameraFollowOn, showAutoTrack)
  }, [cameraFollowOn, showAutoTrack])

  // Focus / rail collapse changes map width — Mapbox must resize or a black gutter
  // appears where the canvas kept the old width.
  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      mapRef.current?.resize()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /**
   * Swap plates only when the style URL changes.
   * setStyle with the same URL does not fire style.load, and the cover stays up.
   */
  const swapMapStyle = (
    map: mapboxgl.Map,
    nextUrl: string,
    token: string,
  ): (() => void) | undefined => {
    mapboxgl.accessToken = token
    if (!nextUrl || nextUrl === createdStyleUrlRef.current) return undefined
    createdStyleUrlRef.current = nextUrl
    styleReadyRef.current = false
    setStylePaintReady(false)
    map.setStyle(nextUrl)
    let loaded = false
    const onLoad = (): void => {
      loaded = true
      ensureOverlayLayers(map)
      rememberLineLayerIds(map)
      styleReadyRef.current = true
      setStylePaintReady(true)
      if (mapApiRef) {
        mapApiRef.current = bindMapApi(map, programmaticMoveRef, getLiveDragPose)
      }
      setStyleEpoch((n) => n + 1)
    }
    const onError = (): void => {
      if (loaded) return
      styleReadyRef.current = true
      setStylePaintReady(true)
    }
    map.once('style.load', onLoad)
    map.once('error', onError)
    return () => {
      map.off('style.load', onLoad)
      map.off('error', onError)
    }
  }

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const detach = swapMapStyle(map, styleUrl, accessToken)
    return () => {
      detach?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleUrl, accessToken])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Home / foundation reset — skip while a director beat is driving (incl. soft-hold).
    // Do not depend on directedCamera here: turning Auto-pan off must leave the live view,
    // not snap back to session.camera (the wide board default).
    if (directedCameraRef.current) return
    map.jumpTo({ center: [camera.lng, camera.lat], zoom: camera.zoom })
  }, [camera.lng, camera.lat, camera.zoom, viewResetId])

  // Auto-pan off (or beats cleared): freeze mid-ease; keep whatever framing is on screen.
  useEffect(() => {
    const map = mapRef.current
    const on = directedCamera != null
    if (hadDirectedCameraRef.current && !on && map) {
      try {
        map.stop()
      } catch {
        /* ignore */
      }
    }
    hadDirectedCameraRef.current = on
  }, [directedCamera])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const onUserCamera = () => {
      if (programmaticMoveRef.current) return
      onUserCameraGestureRef.current?.()
    }
    let lastFrame = 0
    let pointerDown = false
    let clickSeq = 0
    const pointerPx: { current: { x: number; y: number } | null } = { current: null }
    const hoverAt = (event: {
      point: { x: number; y: number }
      originalEvent?: MouseEvent
    }): { id?: string; tip?: string; grab: boolean } => {
      const under = event.originalEvent
        ? document.elementFromPoint(event.originalEvent.clientX, event.originalEvent.clientY)
        : null
      if (under instanceof Element) {
        const mark = under.closest('.sc-mark')
        if (mark instanceof HTMLElement) {
          const id = mark.dataset.id || undefined
          const tip = mark.dataset.tip?.trim() || undefined
          const obj = id ? objectsRef.current.find((item) => item.id === id) : undefined
          const grab = obj?.type === 'sketch' && objectLineSegments(obj).length > 0
          return { id, tip, grab }
        }
      }
      const layerTip = (layerId: string, labelOf: (feature: mapboxgl.MapboxGeoJSONFeature) => string) => {
        try {
          if (!map.getLayer(layerId)) return null
        } catch {
          return null
        }
        let hits: mapboxgl.MapboxGeoJSONFeature[] = []
        try {
          hits = map.queryRenderedFeatures([event.point.x, event.point.y], { layers: [layerId] })
        } catch {
          return null
        }
        const labels = [...new Set(hits.map(labelOf).filter(Boolean))]
        if (!labels.length) return null
        const id = String(hits[0]?.properties?.id ?? '') || undefined
        return { id, tip: labels.join(' · '), grab: false }
      }
      const zone = layerTip('sc-zones-fill', (feature) => String(feature.properties?.tip ?? ''))
      if (zone) return zone
      const plume = layerTip('sc-plumes-fill', (feature) => {
        const source = String(feature.properties?.windSource ?? '')
        if (source === 'tornado') return 'Tornado wind'
        if (source === 'hurricane') return 'Hurricane wind'
        if (source === 'surface') return 'Surface wind'
        return ''
      })
      if (plume) return plume
      const lineId = nearestBoardLineId(map, event.point, objectsRef.current, 16, true)
      if (!lineId) return { grab: false }
      const line = objectsRef.current.find((item) => item.id === lineId)
      const tip =
        line?.description?.trim() ||
        line?.label?.trim() ||
        line?.mapLabel?.trim() ||
        (line ? paletteLabel(line.type) : '')
      return {
        id: lineId,
        tip: tip || undefined,
        grab: line?.type === 'sketch',
      }
    }
    const hoverRef: { current: { id?: string; tip?: string; grab: boolean } } = { current: { grab: false } }
    let heldPointer: { lng: number; lat: number } | null = null
    const emitFrame = (samplePointer = false, immediate = false): void => {
      // Auto-pan is part of Watch. Followers ignore this callback unless they are leading.
      if (!immediate) {
        const now = performance.now()
        // Object drag needs denser poses than pan/hover — 100ms left P's icon far behind the hand.
        const minGap = draggingIdsRef.current.size > 0 ? 32 : 100
        if (now - lastFrame < minGap) return
        lastFrame = now
      } else {
        lastFrame = performance.now()
      }
      const center = map.getCenter()
      const px = pointerPx.current
      if (samplePointer && px) {
        const sampled = map.unproject([px.x, px.y])
        heldPointer = { lng: sampled.lng, lat: sampled.lat }
      } else if (!px) {
        heldPointer = null
      }
      const pointer = heldPointer
      const cursor = hoverRef.current.grab
        ? pointerDown
          ? 'grabbing'
          : 'grab'
        : hoverRef.current.id
          ? 'pointer'
          : pointerDown
            ? 'grabbing'
            : 'grab'
      watchPointerRef.current = {
        px,
        down: pointerDown,
        clickSeq,
        hoverId: hoverRef.current.id,
        hoverTip: hoverRef.current.tip,
        grab: hoverRef.current.grab,
        cursor,
      }
      const dragId =
        draggingIdsRef.current.size === 1 ? [...draggingIdsRef.current][0] : undefined
      const dragMarker = dragId ? markersRef.current.get(`id:${dragId}`)?.getLngLat() : undefined
      const dragLive =
        dragId && plumeDragRef.current?.id === dragId ? plumeDragRef.current : null
      const dragLng = dragMarker?.lng ?? dragLive?.liveLng
      const dragLat = dragMarker?.lat ?? dragLive?.liveLat
      onCameraFrameRef.current?.({
        lng: center.lng,
        lat: center.lat,
        zoom: map.getZoom(),
        focusId: selectedIdRef.current ?? '',
        ...(dragId && dragLng != null && dragLat != null
          ? { dragId, dragLng, dragLat }
          : {}),
        ...(pointer
          ? {
              pointerLng: pointer.lng,
              pointerLat: pointer.lat,
              pointerDown,
              clickSeq,
              hoverId: hoverRef.current.id,
              hoverTip: hoverRef.current.tip,
              grab: hoverRef.current.grab,
              cursor,
            }
          : {}),
      })
    }
    const canvasPoint = (event: PointerEvent): { x: number; y: number } | null => {
      const rect = map.getCanvas().getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null
      return { x, y }
    }
    const trackPointer = (event: PointerEvent, immediate = false): void => {
      const target = event.target
      const overMap = target instanceof Node && map.getContainer().contains(target)
      const pt = overMap ? canvasPoint(event) : null
      if (!pt) {
        if (pointerDown) return
        if (!pointerPx.current) return
        pointerPx.current = null
        hoverRef.current = { grab: false }
        emitFrame(false, true)
        return
      }
      pointerPx.current = pt
      hoverRef.current = hoverAt({ point: pt, originalEvent: event })
      emitFrame(true, immediate)
    }
    const onMove = (): void => {
      // Camera only. Re-reading the pixel here turns a pan into a flying cursor
      // on a map that is not following that pan.
      emitFrame(false)
    }
    const onMoveEnd = (): void => {
      emitFrame(false, true)
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return
      const pt = canvasPoint(event)
      if (!pt) return
      pointerPx.current = pt
      pointerDown = true
      clickSeq += 1
      hoverRef.current = hoverAt({ point: pt, originalEvent: event })
      emitFrame(true, true)
    }
    const onPointerUp = (): void => {
      if (!pointerDown) return
      pointerDown = false
      emitFrame(false, true)
    }
    map.on('dragstart', onUserCamera)
    map.on('zoomstart', onUserCamera)
    map.on('rotatestart', onUserCamera)
    map.on('pitchstart', onUserCamera)
    map.on('move', onMove)
    map.on('moveend', onMoveEnd)
    map.getContainer().addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('pointermove', trackPointer)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    return () => {
      map.getContainer().removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('pointermove', trackPointer)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      map.off('dragstart', onUserCamera)
      map.off('zoomstart', onUserCamera)
      map.off('rotatestart', onUserCamera)
      map.off('pitchstart', onUserCamera)
      map.off('move', onMove)
      map.off('moveend', onMoveEnd)
    }
  }, [styleEpoch])

  // A click selects after this frame's mousedown. Send the new object on the next paint.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    const held = watchPointerRef.current
    const center = map.getCenter()
    const pointer = held.px ? map.unproject([held.px.x, held.px.y]) : null
    // Include live drag pose — a focus-only frame used to wipe dragId in the Watch
    // coalesce buffer (P hand kept moving, icon froze at the last drag pose).
    const dragId =
      draggingIdsRef.current.size === 1 ? [...draggingIdsRef.current][0] : undefined
    const dragMarker = dragId ? markersRef.current.get(`id:${dragId}`)?.getLngLat() : undefined
    const dragLive =
      dragId && plumeDragRef.current?.id === dragId ? plumeDragRef.current : null
    const dragLng = dragMarker?.lng ?? dragLive?.liveLng
    const dragLat = dragMarker?.lat ?? dragLive?.liveLat
    onCameraFrameRef.current?.({
      lng: center.lng,
      lat: center.lat,
      zoom: map.getZoom(),
      focusId: selectedId ?? '',
      ...(dragId && dragLng != null && dragLat != null
        ? { dragId, dragLng, dragLat }
        : {}),
      ...(pointer
        ? {
            pointerLng: pointer.lng,
            pointerLat: pointer.lat,
            pointerDown: held.down,
            clickSeq: held.clickSeq,
            hoverId: held.hoverId,
            hoverTip: held.hoverTip,
            grab: held.grab,
            cursor: held.cursor,
          }
        : {}),
    })
  }, [selectedId])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    map.getContainer().querySelector('.sc-watch-trail')?.remove()
    const markers = watchCursorMarkersRef.current
    const nextIds = new Set(watchCursors.map((c) => c.id))
    for (const [id, marker] of markers) {
      if (!nextIds.has(id)) {
        marker.remove()
        markers.delete(id)
      }
    }
    for (const watchCursor of watchCursors) {
      const point: [number, number] = [watchCursor.lng, watchCursor.lat]
      let cursor = markers.get(watchCursor.id)
      if (!cursor) {
        const el = document.createElement('div')
        el.className = 'sc-watch-cursor'
        el.innerHTML = WATCH_GRAB
        // setLngLat before addTo. addTo projects immediately, and a marker with no
        // location throws and takes the page down. Anchor is the element's top-left;
        // the glyph offset puts the fingertip or the palm on that point.
        cursor = new mapboxgl.Marker({ element: el, anchor: 'top-left' }).setLngLat(point)
        markers.set(watchCursor.id, cursor)
        try {
          cursor.addTo(map)
        } catch {
          cursor.remove()
          markers.delete(watchCursor.id)
          continue
        }
      } else {
        cursor.setLngLat(point)
      }
      const el = cursor.getElement()
      const glyph =
        watchCursor.cursor ??
        (watchCursor.grab
          ? watchCursor.down
            ? 'grabbing'
            : 'grab'
          : watchCursor.hoverId
            ? 'pointer'
            : watchCursor.down
              ? 'grabbing'
              : 'grab')
      if (el.dataset.glyph !== glyph) {
        el.dataset.glyph = glyph
        const svg = glyph === 'pointer' ? WATCH_POINTER : glyph === 'grabbing' ? WATCH_GRABBING : WATCH_GRAB
        el.innerHTML = svg
      }
      cursor.setOffset(glyph === 'pointer' ? WATCH_POINTER_OFFSET : WATCH_HAND_OFFSET)
      // Color the hand/pointer itself — no overlapping initials badge (lineup + tip identify who).
      const who = watchCursor.who?.trim() ?? ''
      el.querySelector('.sc-watch-who')?.remove()
      if (who) {
        const tone = presenceToneForInitials(who)
        el.style.color = tone.ring
        el.dataset.who = who
      } else {
        el.style.color = '#fff'
        delete el.dataset.who
      }
      const cue = watchCursor.zoomCue
      const cueKey = cue ? `${cue}:${watchCursor.zoomCueN ?? 0}` : ''
      let zoomEl = el.querySelector('.sc-watch-zoom')
      if (!cue) {
        zoomEl?.remove()
        delete el.dataset.zoomCue
      } else if (el.dataset.zoomCue !== cueKey) {
        zoomEl?.remove()
        zoomEl = document.createElement('span')
        zoomEl.className = `sc-watch-zoom ${cue}`
        el.appendChild(zoomEl)
        el.dataset.zoomCue = cueKey
      }
    }
    watchTipRef.current?.remove()
    watchTipRef.current = null
  }, [watchCursors, styleEpoch])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !directedCamera) return
    // Soft-hold: keep the user's live framing so they can Save camera on this card.
    if (directedCamera.softHold) return

    const opts = {
      center: [directedCamera.lng, directedCamera.lat] as [number, number],
      zoom: directedCamera.zoom,
    }
    try {
      map.stop()
    } catch {
      /* map may not be fully ready */
    }
    programmaticMoveRef.current = true
    const clearProgrammatic = (): void => {
      programmaticMoveRef.current = false
    }
    // Always clear on a timer — moveend/idle are unreliable for no-op jumps and can stick.
    window.setTimeout(clearProgrammatic, Math.max(50, directedCamera.easeMs + 50))

    try {
      if (directedCamera.instant || directedCamera.easeMs <= 0) {
        map.jumpTo(opts)
        return
      }
      map.easeTo({
        ...opts,
        duration: Math.max(0, directedCamera.easeMs),
        essential: true,
      })
    } catch {
      programmaticMoveRef.current = false
    }
    // beatKey / softHold are the identity of the active drive; styleEpoch re-applies after style reload.
    // Include cameraFollowOn so OFF→ON immediately eases to the current beat (not wait for next change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    directedCamera?.beatKey,
    directedCamera?.instant,
    directedCamera?.easeMs,
    directedCamera?.softHold,
    cameraFollowOn,
    styleEpoch,
  ])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    const setHlPaint = (dim: boolean) => {
      if (map.getLayer('sc-pipe-hl')) {
        map.setPaintProperty('sc-pipe-hl', 'line-color', '#5EEAD4')
        map.setPaintProperty('sc-pipe-hl', 'line-width', dim ? 6.5 : 5)
        map.setPaintProperty('sc-pipe-hl', 'line-opacity', dim ? 0.92 : 0.5)
      }
      if (map.getLayer('sc-pipe-hl-hot')) {
        map.setPaintProperty('sc-pipe-hl-hot', 'line-color', '#8EF0E0')
        map.setPaintProperty('sc-pipe-hl-hot', 'line-width', dim ? 8 : 7)
        map.setPaintProperty('sc-pipe-hl-hot', 'line-opacity', dim ? 0.62 : 0.5)
      }
    }
    setHlPaint(focusDim)
    // Waypoint inspect clears asset highlight so the route stays readable.
    if (waypointObjectId) {
      setGeo(map, SRC_HL, EMPTY_FC)
      setGeo(map, SRC_HL_HOT, EMPTY_FC)
      return
    }
    // Base cluster = linked / checked picks. Hover emphasis is a lighter shade on top.
    if (attachedHighlightObjects.length) {
      setGeo(map, SRC_HL, attachedHighlightFeatures(attachedHighlightObjects))
    } else {
      setGeo(map, SRC_HL, EMPTY_FC)
    }
    if (mapFeatureHighlight) {
      setGeo(map, SRC_HL_HOT, hotHighlightData(mapFeatureHighlight))
    } else {
      setGeo(map, SRC_HL_HOT, EMPTY_FC)
    }
  }, [mapFeatureHighlight, attachedHighlightObjects, styleEpoch, waypointObjectId, focusDim])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    ensureOverlayLayers(map)
    if (map.getLayer('sc-zones-fill')) {
      map.setLayoutProperty('sc-zones-fill', 'fill-sort-key', [
        'case',
        ['any', ['==', ['get', 'kind'], 'tornado-dir'], ['==', ['get', 'kind'], 'hurricane-dir']],
        2,
        1,
      ])
      map.setPaintProperty('sc-zones-fill', 'fill-color', [
        'match',
        ['get', 'kind'],
        'fire',
        '#FF4D00',
        'brwn',
        '#8B5A2B',
        'tornado-dir',
        '#E4D4FF',
        'hurricane-dir',
        '#D6E6FA',
        'tornado',
        '#A56BFF',
        'hurricane',
        '#3B7DD8',
        '#FF8C1A',
      ])
      map.setPaintProperty('sc-zones-fill', 'fill-opacity', [
        'case',
        ['any', ['==', ['get', 'kind'], 'tornado-dir'], ['==', ['get', 'kind'], 'hurricane-dir']],
        0.95,
        ['==', ['get', 'selected'], 1],
        ['match', ['get', 'kind'], 'fire', 0.52, 'brwn', 0.5, 'tornado', 0.46, 'hurricane', 0.48, 0.48],
        ['==', ['get', 'lit'], 1],
        ['match', ['get', 'kind'], 'fire', 0.32, 'brwn', 0.3, 'tornado', 0.2, 'hurricane', 0.26, 0.26],
        0.12,
      ])
    }
    if (map.getLayer('sc-zones-line')) {
      map.setFilter('sc-zones-line', ['all', ['!=', ['get', 'kind'], 'tornado-dir'], ['!=', ['get', 'kind'], 'hurricane-dir']])
      map.setPaintProperty('sc-zones-line', 'line-color', [
        'match',
        ['get', 'kind'],
        'fire',
        '#FF4D00',
        'brwn',
        '#A0522D',
        'tornado',
        '#C9A6FF',
        'hurricane',
        '#8FB4E8',
        '#FF8C1A',
      ])
      map.setPaintProperty('sc-zones-line', 'line-width', [
        'case',
        ['==', ['get', 'selected'], 1],
        4.5,
        ['==', ['get', 'lit'], 1],
        2.5,
        1.75,
      ])
      map.setPaintProperty('sc-zones-line', 'line-opacity', [
        'case',
        ['==', ['get', 'selected'], 1],
        1,
        0.55,
      ])
    }
    if (map.getLayer('sc-plumes-fill')) {
      map.setPaintProperty('sc-plumes-fill', 'fill-color', [
        'match',
        ['get', 'windSource'],
        'surface',
        PLUME_PAINT.surface.fill,
        'hurricane',
        PLUME_PAINT.hurricane.fill,
        PLUME_PAINT.tornado.fill,
      ])
      map.setPaintProperty('sc-plumes-fill', 'fill-opacity', 0.5)
    }
    if (map.getSource(SRC_PLUMES) && !map.getLayer('sc-plumes-line')) {
      map.addLayer({
        id: 'sc-plumes-line',
        type: 'line',
        source: SRC_PLUMES,
        paint: {
          'line-color': [
            'match',
            ['get', 'windSource'],
            'surface',
            PLUME_PAINT.surface.line,
            'hurricane',
            PLUME_PAINT.hurricane.line,
            PLUME_PAINT.tornado.line,
          ],
          'line-width': 2,
          'line-opacity': 0.9,
        },
      })
    } else if (map.getLayer('sc-plumes-line')) {
      map.setPaintProperty('sc-plumes-line', 'line-color', [
        'match',
        ['get', 'windSource'],
        'surface',
        PLUME_PAINT.surface.line,
        'hurricane',
        PLUME_PAINT.hurricane.line,
        PLUME_PAINT.tornado.line,
      ])
    }
    // Waypoint inspect: only the selected object's overlays — hide other radii, roads, sketches.
    const overlayObjects = waypointObjectId
      ? objects.filter((o) => o.id === waypointObjectId)
      : objects
    const watchLit = [
      ...watchCursors.map((c) => c.hoverId),
      watchFocusId,
    ].filter((id): id is string => Boolean(id))
    const litIds = watchLit.length ? [...highlightIds, ...watchLit] : highlightIds
    const liveDrag = plumeDragRef.current
    const atLiveDrag = (list: readonly ResolvedObject[]): readonly ResolvedObject[] =>
      liveDrag
        ? list.map((object) =>
            object.id === liveDrag.id
              ? { ...object, pose: { lng: liveDrag.liveLng, lat: liveDrag.liveLat } }
              : object,
          )
        : list
    const zoneFc = iconsOnly ? EMPTY_FC : zoneFeatures(atLiveDrag(overlayObjects), litIds, selectedId)
    zoneFcRef.current = zoneFc
    setGeo(map, SRC_ZONES, zoneFc)
    // Path isolation also hides foreign plumes (same idea as tornado plume focus).
    const plumeObjects = plumeFocusObjectId
      ? objects.filter((o) => o.id === plumeFocusObjectId)
      : waypointObjectId
        ? []
        : objects
    const plumeFc = iconsOnly ? EMPTY_FC : plumeFeatures(atLiveDrag(plumeObjects), { show: surfacePlumes })
    plumeFcRef.current = plumeFc
    setGeo(map, SRC_PLUMES, plumeFc)
    if (!strokeDragRef.current) {
      setGeo(
        map,
        SRC_LINES,
        iconsOnly ? EMPTY_FC : lineFeatures(overlayObjects, selectedId, litIds),
      )
    }
    setGeo(map, SRC_FOCUS, iconsOnly ? EMPTY_FC : focusFeatures(objects, highlightIds))
    // Don't clobber live drag geometry — mouseup commits via onWaypointMove.
    if (draggingWaypointRef.current == null) {
      setGeo(
        map,
        SRC_KF,
        waypointObjectId
          ? keyframeOverlayFeatures(objects, waypointObjectId, waypointFocusIndex)
          : EMPTY_FC,
      )
    }
    // Overlay paint resets can undim layers — re-apply allowlist dim while focus is active.
    if (focusDimRef.current) {
      focusDimBackupRef.current = applyFocusDimPaint(
        map,
        true,
        focusDimBackupRef.current,
        focusDimKeepRef.current,
      )
      syncBasemapVeil(map, true)
    }
  }, [
    objects,
    highlightIds,
    selectedId,
    watchCursors,
    watchFocusId,
    styleEpoch,
    waypointObjectId,
    plumeFocusObjectId,
    waypointFocusIndex,
    surfacePlumes,
    iconsOnly,
  ])

  const onPlumeHoverRef = useRef(onPlumeHover)
  onPlumeHoverRef.current = onPlumeHover

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    let plumesReady = false
    try {
      plumesReady = Boolean(map.getLayer('sc-plumes-fill'))
    } catch {
      return
    }
    if (!plumesReady) return
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'sc-plume-tip',
      offset: 10,
    })
    const followTip = (lng: number, lat: number): void => {
      popup.setLngLat([lng, lat]).addTo(map)
    }
    const hidePlume = (): void => {
      if (followOverlayTipRef.current === followTip) followOverlayTipRef.current = null
      map.getContainer().classList.remove('plume-hover')
      popup.remove()
      onPlumeHoverRef.current?.(null)
    }
    underlayTipHiders.add(hidePlume)
    const onMove = (ev: mapboxgl.MapLayerMouseEvent): void => {
      const hovered = document.elementFromPoint(ev.originalEvent.clientX, ev.originalEvent.clientY)
      if (hovered instanceof Element && hovered.closest('.sc-mark')) {
        hidePlume()
        return
      }
      map.getContainer().classList.add('plume-hover')
      const hits = map.queryRenderedFeatures(ev.point, { layers: ['sc-plumes-fill'] })
      const labels = [
        ...new Set(
          hits
            .map((f) => {
              const source = String(f.properties?.windSource ?? '')
              if (source === 'tornado') return 'Tornado wind'
              if (source === 'hurricane') return 'Hurricane wind'
              if (source === 'surface') return 'Surface wind'
              return ''
            })
            .filter(Boolean),
        ),
      ]
      if (!labels.length) {
        map.getContainer().classList.remove('plume-hover')
        popup.remove()
        onPlumeHoverRef.current?.(null)
        return
      }
      const hasSurface = labels.includes('Surface wind')
      const hasTornado = labels.includes('Tornado wind')
      const hasHurricane = labels.includes('Hurricane wind')
      onPlumeHoverRef.current?.(
        hasSurface && (hasTornado || hasHurricane)
          ? 'both'
          : hasSurface
            ? 'surface'
            : hasTornado
              ? 'tornado'
              : hasHurricane
                ? 'hurricane'
                : null,
      )
      popup.setLngLat(ev.lngLat).setText(labels.join(' · ')).addTo(map)
      followOverlayTipRef.current = followTip
    }
    const onLeave = (): void => {
      hidePlume()
    }
    const canvas = map.getCanvas()
    let plumeGesture = false
    const onCanvasDown = (event: Event): void => {
      if (plumeGesture) return
      if (!(event instanceof MouseEvent)) return
      if (!(event.target instanceof Node) || !canvas.contains(event.target)) return
      const under = document.elementFromPoint(event.clientX, event.clientY)
      if (under instanceof Element && under.closest('.sc-mark, .sc-watch-cursor, .sc-watch-trail')) return
      // A palette tool drops at the cursor. A plume or ring under the point must not eat that click.
      if (placeArmedRef.current) return
      const rect = canvas.getBoundingClientRect()
      const point: [number, number] = [event.clientX - rect.left, event.clientY - rect.top]
      // Position marks sit on the wind field. The field's press would otherwise drag the whole storm.
      if (waypointObjectIdRef.current) {
        const kfLayers = ['sc-kf-hit', 'sc-kf-point', 'sc-kf-label'].filter((id) => {
          try {
            return Boolean(map.getLayer(id))
          } catch {
            return false
          }
        })
        if (kfLayers.length) {
          try {
            if (map.queryRenderedFeatures(point, { layers: kfLayers }).length) return
          } catch {
            /* layer may be mid-style */
          }
        }
      }
      const layers = ['sc-plumes-fill', 'sc-zones-fill'].filter((id) => {
        try {
          return Boolean(map.getLayer(id))
        } catch {
          return false
        }
      })
      if (!layers.length) return
      let hits: mapboxgl.MapboxGeoJSONFeature[] = []
      try {
        hits = map.queryRenderedFeatures(point, { layers })
      } catch {
        return
      }
      const id = String(hits[0]?.properties?.id ?? '')
      if (!id) return
      // Mapbox GL 3 starts a pan on pointerdown. Stop it before the container sees the event.
      event.preventDefault()
      event.stopPropagation()
      // The map click that follows this press would clear the selection.
      armSuppressLineClickRef.current()
      onPipeRef.current(null)
      plumeGesture = true
      const panWasEnabled = map.dragPan.isEnabled()
      if (panWasEnabled) map.dragPan.disable()
      const allowed = canDragObjectRef.current ? canDragObjectRef.current(id) : editingRef.current
      const alreadySelected = selectedIdRef.current === id
      // A second click keeps the selection. Empty map is what clears it.
      if (!alreadySelected) onSelectRef.current(id)
      const marker = markersRef.current.get(`id:${id}`)
      const pointer = map.unproject(point)
      const origin = marker?.getLngLat() ?? pointer
      const start = { lng: pointer.lng, lat: pointer.lat }
      const startPx = { x: event.clientX, y: event.clientY }
      let prev = { lng: origin.lng, lat: origin.lat }
      let moved = false
      if (allowed) {
        draggingIdsRef.current.add(id)
        dragFromRef.current.set(id, prev)
        plumeDragRef.current = {
          id,
          lng: origin.lng,
          lat: origin.lat,
          liveLng: origin.lng,
          liveLat: origin.lat,
          base: plumeFcRef.current,
        }
      }
      const shiftOwned = (sourceId: string, dLng: number, dLat: number, base: FeatureCollection): void => {
        if (!map.getSource(sourceId)) return
        setGeo(map, sourceId, {
          type: 'FeatureCollection',
          features: base.features.map((feature) => {
            if (feature.properties?.id !== id || !feature.geometry) return feature
            return { ...feature, geometry: shiftPlumeGeometry(feature.geometry, dLng, dLat) }
          }),
        })
      }
      const zoneBase = zoneFcRef.current
      const onDrag = (move: PointerEvent): void => {
        if (!allowed) return
        if (Math.hypot(move.clientX - startPx.x, move.clientY - startPx.y) > 3) moved = true
        const box = map.getCanvas().getBoundingClientRect()
        const at = map.unproject([move.clientX - box.left, move.clientY - box.top])
        const lng = origin.lng + (at.lng - start.lng)
        const lat = origin.lat + (at.lat - start.lat)
        marker?.setLngLat([lng, lat])
        if (plumeDragRef.current?.id === id) {
          plumeDragRef.current.liveLng = lng
          plumeDragRef.current.liveLat = lat
        }
        followOverlayTipRef.current?.(at.lng, at.lat)
        onMoveRef.current(id, lng, lat, prev)
        prev = { lng, lat }
        dragFromRef.current.set(id, prev)
        const drag = plumeDragRef.current
        if (drag && drag.id === id) {
          const dLng = lng - drag.lng
          const dLat = lat - drag.lat
          setGeo(
            map,
            SRC_PLUMES,
            plumesFollowingDrag(drag.base, plumeFcRef.current, id, dLng, dLat),
          )
          shiftOwned(SRC_ZONES, dLng, dLat, zoneBase)
        }
        // Emit Watch from this handler (marker already updated). trackPointer alone raced
        // and often sampled the pre-move pose; peers saw the hand lead the icon.
        const now = performance.now()
        if (now - dragWatchEmitAtRef.current >= 32) {
          dragWatchEmitAtRef.current = now
          const center = map.getCenter()
          onCameraFrameRef.current?.({
            lng: center.lng,
            lat: center.lat,
            zoom: map.getZoom(),
            focusId: selectedIdRef.current ?? id,
            dragId: id,
            dragLng: lng,
            dragLat: lat,
            pointerLng: at.lng,
            pointerLat: at.lat,
            pointerDown: true,
            hoverId: id,
            grab: true,
            cursor: 'grabbing',
          })
        }
      }
      const onUp = (): void => {
        plumeGesture = false
        window.removeEventListener('pointermove', onDrag)
        window.removeEventListener('pointerup', onUp, true)
        window.removeEventListener('pointercancel', onUp, true)
        // Final pose before clearing drag ids (coalesce + Upsert flush on parent).
        {
          const center = map.getCenter()
          const tip = marker?.getLngLat()
          if (tip) {
            onCameraFrameRef.current?.({
              lng: center.lng,
              lat: center.lat,
              zoom: map.getZoom(),
              focusId: selectedIdRef.current ?? id,
              dragId: id,
              dragLng: tip.lng,
              dragLat: tip.lat,
              pointerLng: tip.lng,
              pointerLat: tip.lat,
              pointerDown: true,
              hoverId: id,
              grab: true,
              cursor: 'grabbing',
            })
          }
        }
        draggingIdsRef.current.delete(id)
        dragFromRef.current.delete(id)
        if (plumeDragRef.current?.id === id) plumeDragRef.current = null
        if (
          panWasEnabled &&
          !freehandRef.current &&
          !penRef.current &&
          !rulerRef.current &&
          !scribbleRef.current
        ) {
          map.dragPan.enable()
        }
        if (moved) onObjectMoveEndRef.current?.()
        // Mapbox fires its click from the canvas mouseup, which runs before a
        // bubble listener on window. Capture runs first, so a slow press still
        // swallows that click instead of opening Settings.
        armSuppressLineClickRef.current()
      }
      window.addEventListener('pointermove', onDrag)
      window.addEventListener('pointerup', onUp, true)
      window.addEventListener('pointercancel', onUp, true)
    }
    map.on('mousemove', 'sc-plumes-fill', onMove)
    map.on('mouseleave', 'sc-plumes-fill', onLeave)
    canvas.addEventListener('pointerdown', onCanvasDown, true)
    map.getContainer().addEventListener('pointerdown', onCanvasDown, true)
    return () => {
      underlayTipHiders.delete(hidePlume)
      map.off('mousemove', 'sc-plumes-fill', onMove)
      map.off('mouseleave', 'sc-plumes-fill', onLeave)
      canvas.removeEventListener('pointerdown', onCanvasDown, true)
      map.getContainer().removeEventListener('pointerdown', onCanvasDown, true)
      hidePlume()
    }
  }, [styleEpoch])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    let zonesReady = false
    try {
      zonesReady = Boolean(map.getLayer('sc-zones-fill'))
    } catch {
      return
    }
    if (!zonesReady) return
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'sc-plume-tip',
      offset: 10,
    })
    const followTip = (lng: number, lat: number): void => {
      popup.setLngLat([lng, lat]).addTo(map)
    }
    const hideZone = (): void => {
      if (followOverlayTipRef.current === followTip) followOverlayTipRef.current = null
      map.getContainer().classList.remove('overlay-tip')
      popup.remove()
    }
    underlayTipHiders.add(hideZone)
    const onMove = (ev: mapboxgl.MapLayerMouseEvent): void => {
      const hovered = document.elementFromPoint(ev.originalEvent.clientX, ev.originalEvent.clientY)
      if (hovered instanceof Element && hovered.closest('.sc-mark')) {
        hideZone()
        return
      }
      const hits = map.queryRenderedFeatures(ev.point, { layers: ['sc-zones-fill'] })
      const labels = [
        ...new Set(
          hits.map((feature) => String(feature.properties?.tip ?? '')).filter(Boolean),
        ),
      ]
      if (!labels.length) {
        map.getContainer().classList.remove('overlay-tip')
        popup.remove()
        return
      }
      map.getContainer().classList.add('overlay-tip')
      popup.setLngLat(ev.lngLat).setText(labels.join(' · ')).addTo(map)
      followOverlayTipRef.current = followTip
    }
    const onLeave = (): void => {
      hideZone()
    }
    map.on('mousemove', 'sc-zones-fill', onMove)
    map.on('mouseleave', 'sc-zones-fill', onLeave)
    return () => {
      underlayTipHiders.delete(hideZone)
      map.off('mousemove', 'sc-zones-fill', onMove)
      map.off('mouseleave', 'sc-zones-fill', onLeave)
      hideZone()
    }
  }, [styleEpoch])

  // Waypoint hit-test + drag (GeoJSON points — stay locked to the map on zoom).
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    if (!waypointObjectId) {
      map.getCanvas().style.cursor = ''
      return
    }

    const pointLayers = ['sc-kf-hit', 'sc-kf-point', 'sc-kf-label'].filter((id) => map.getLayer(id))

    const indexFromEvent = (ev: mapboxgl.MapLayerMouseEvent): number | null => {
      const hits = ev.features ?? []
      if (!hits.length) return null
      // Prefer the topmost co-located pin (highest z) when several share a click.
      let best = hits[0]!
      let bestZ = Number(best.properties?.z ?? -1)
      for (let i = 1; i < hits.length; i++) {
        const hit = hits[i]!
        const z = Number(hit.properties?.z ?? -1)
        if (z > bestZ) {
          bestZ = z
          best = hit
        }
      }
      const raw = best.properties?.index
      const n = typeof raw === 'number' ? raw : Number(raw)
      if (!Number.isFinite(n) || n < 1) return null
      return n - 1
    }

    const onEnter = (): void => {
      map.getCanvas().style.cursor = waypointsEditable ? 'grab' : 'pointer'
    }
    const onLeave = (): void => {
      if (draggingWaypointRef.current == null) map.getCanvas().style.cursor = ''
    }
    const onClickPoint = (ev: mapboxgl.MapLayerMouseEvent): void => {
      const idx = indexFromEvent(ev)
      if (idx == null) return
      ev.originalEvent.stopPropagation()
      onWaypointFocusRef.current?.(idx)
    }
    const onDown = (ev: mapboxgl.MapLayerMouseEvent): void => {
      if (!waypointsEditable) return
      const idx = indexFromEvent(ev)
      if (idx == null) return
      ev.preventDefault()
      draggingWaypointRef.current = idx
      onWaypointFocusRef.current?.(idx)
      map.getCanvas().style.cursor = 'grabbing'
      map.dragPan.disable()
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      const idx = draggingWaypointRef.current
      if (idx == null) return
      // Live-update GeoJSON while dragging so the pin tracks the cursor.
      const frames = orderedPoseKeyframes(objectsRef.current, waypointObjectId)
      if (!frames[idx]) return
      let start = idx
      let end = idx
      while (start > 0 && posesSameSpot(frames[start - 1]!, frames[start]!)) start -= 1
      while (end < frames.length - 1 && posesSameSpot(frames[end]!, frames[end + 1]!)) end += 1
      const live = frames.map((f, i) =>
        i >= start && i <= end ? { ...f, lng: ev.lngLat.lng, lat: ev.lngLat.lat } : f,
      )
      const obj = objectsRef.current.find((o) => o.id === waypointObjectId)
      if (!obj) return
      const fc = keyframeOverlayFeatures(
        [{ ...obj, keyframes: live }],
        waypointObjectId,
        idx,
      )
      setGeo(map, SRC_KF, fc)
    }
    const onUp = (ev: mapboxgl.MapMouseEvent): void => {
      const idx = draggingWaypointRef.current
      if (idx == null) return
      draggingWaypointRef.current = null
      map.dragPan.enable()
      map.getCanvas().style.cursor = ''
      onWaypointMoveRef.current?.(idx, ev.lngLat.lng, ev.lngLat.lat)
    }

    for (const layer of pointLayers) {
      map.on('mouseenter', layer, onEnter)
      map.on('mouseleave', layer, onLeave)
      map.on('click', layer, onClickPoint)
      map.on('mousedown', layer, onDown)
    }
    map.on('mousemove', onMove)
    map.on('mouseup', onUp)

    return () => {
      for (const layer of pointLayers) {
        map.off('mouseenter', layer, onEnter)
        map.off('mouseleave', layer, onLeave)
        map.off('click', layer, onClickPoint)
        map.off('mousedown', layer, onDown)
      }
      map.off('mousemove', onMove)
      map.off('mouseup', onUp)
      map.dragPan.enable()
      draggingWaypointRef.current = null
    }
  }, [
    waypointObjectId,
    waypointsEditable,
    styleEpoch,
  ])

  // Drop any leftover GeoJSON dim veil from older sessions (caused pan ghosts).
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    try {
      if (map.getLayer('sc-dim-veil')) map.removeLayer('sc-dim-veil')
      if (map.getSource('sc-dim')) map.removeSource('sc-dim')
    } catch {
      /* style may be mid-reload */
    }
  }, [styleEpoch])

  // Focus dim: dim every map layer by default; allowlist keeps HL bright (not a denylist of what to dim).
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    focusDimBackupRef.current = applyFocusDimPaint(
      map,
      focusDim,
      focusDimBackupRef.current,
      focusDimKeep,
    )
    syncBasemapVeil(map, focusDim)
    const refreshVeil = (): void => {
      if (focusDimRef.current) syncBasemapVeil(map, true)
    }
    if (focusDim) map.on('move', refreshVeil)
    if (!focusDim && map.getLayer('sc-pipe-hl')) {
      try {
        map.setPaintProperty('sc-pipe-hl', 'line-width', 5)
        map.setPaintProperty('sc-pipe-hl', 'line-opacity', 0.5)
      } catch {
        /* ignore */
      }
    }
    if (!focusDim && map.getLayer('sc-pipe-hl-hot')) {
      try {
        map.setPaintProperty('sc-pipe-hl-hot', 'line-width', 7)
        map.setPaintProperty('sc-pipe-hl-hot', 'line-opacity', 0.5)
      } catch {
        /* ignore */
      }
    }
    return () => {
      map.off('move', refreshVeil)
      const m = mapRef.current
      if (!m) return
      focusDimBackupRef.current = applyFocusDimPaint(m, false, focusDimBackupRef.current)
      syncBasemapVeil(m, false)
    }
  }, [focusDim, styleEpoch, focusDimKeep.join(',')])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const keep = new Set<string>()
    const lit = new Set([...highlightIds, ...iconHighlightIds])
    const flashing = new Set(ghostFlashIds)
    const stacks = new Map<string, { anchor: { lng: number; lat: number }; members: ResolvedObject[] }>()
    for (const obj of objects) {
      // Inspecting waypoints: hide every other marker so co-located units (e.g. Engine 2
      // parked on Engine 1's first waypoint) don't steal hover/clicks.
      if (waypointObjectId && obj.id !== waypointObjectId) continue
      // Waypoints accordion open with pins: hide unit icon so pin 1 isn't buried (pose = kf 0).
      if (waypointObjectId && obj.id === waypointObjectId && hideWaypointUnitIcon) {
        continue
      }
      const lineSegs = objectLineSegments(obj)
      if (obj.type === 'road' && lineSegs.length > 0) continue
      // Lines and sketches are the stroke itself. A pin on the path was a dead blob.
      if (obj.type === 'sketch' && lineSegs.length > 0) continue
      const anchor = objectAnchorLngLat(obj)
      if (!anchor || obj.type === 'canal' || obj.type === 'wind') continue
      // Stable per-object keys — geographic keys recreate the marker mid-drag and kill Mapbox drag.
      stacks.set(`id:${obj.id}`, { anchor, members: [obj] })
    }
    // Attach co-located peers for caption stacking (paint only; marker key stays per-id).
    const byOrigin = new Map<string, ResolvedObject[]>()
    for (const stack of stacks.values()) {
      const obj = stack.members[0]!
      if (obj.type === 'label' || obj.type === 'sketch') continue
      const ok = originKey(stack.anchor)
      const cur = byOrigin.get(ok)
      if (cur) cur.push(obj)
      else byOrigin.set(ok, [obj])
    }
    for (const stack of stacks.values()) {
      const obj = stack.members[0]!
      if (obj.type === 'label' || obj.type === 'sketch') continue
      const peers = byOrigin.get(originKey(stack.anchor))
      if (peers && peers.length > 1) stack.members = peers
    }
    markerPaintRef.current.clear()
    for (const [key, stack] of stacks) {
      const owner = key.startsWith('id:') ? key.slice(3) : stack.members[0]!.id
      const owned = stack.members.find((m) => m.id === owner) ?? stack.members[0]!
      const active = pickStackActive(stack.members, selectedId, highlightIds)
      // Only one visible pin per co-located group (the stack representative).
      const representative = pickStackActive(stack.members, selectedId, highlightIds)
      const isRep = owner === representative.id || stack.members.length === 1
      keep.add(key)
      let marker = markersRef.current.get(key)
      if (!marker) {
        const root = document.createElement('div')
        const inner = document.createElement('div')
        inner.className = 'sc-mark'
        inner.dataset.id = owner
        root.appendChild(inner)
        marker = new mapboxgl.Marker({
          element: root,
          // Center on the fixed face box; captions hang outside and must not move the pin.
          anchor: 'center',
          pitchAlignment: 'viewport',
          rotationAlignment: 'viewport',
          draggable: false,
        })
          .setLngLat([stack.anchor.lng, stack.anchor.lat])
          .addTo(map)
        const created = marker
        // Mapbox marker drag and map pan both claim the same pointerdown, so a
        // press on the icon often never starts. Drag the pin ourselves.
        root.addEventListener('pointerdown', (event) => {
          if (!(event instanceof PointerEvent) || event.button !== 0) return
          const id = inner.dataset.id ?? owner
          const allowed = canDragObjectRef.current
            ? canDragObjectRef.current(id)
            : editingRef.current
          event.preventDefault()
          event.stopPropagation()
          // Preview/playback can walk the pin off the cursor before mouseup; without
          // suppress, the map click that follows lands on empty board and clears Inspect.
          armSuppressLineClickRef.current()
          if (!allowed) {
            onPipeRef.current(null)
            onSelectRef.current(id)
            const rearm = (): void => {
              armSuppressLineClickRef.current()
              window.removeEventListener('pointerup', rearm, true)
              window.removeEventListener('pointercancel', rearm, true)
            }
            window.addEventListener('pointerup', rearm, true)
            window.addEventListener('pointercancel', rearm, true)
            return
          }
          const panWasEnabled = map.dragPan.isEnabled()
          if (panWasEnabled) map.dragPan.disable()
          draggingIdsRef.current.add(id)
          const origin = created.getLngLat()
          const rect = map.getCanvas().getBoundingClientRect()
          const start = map.unproject([event.clientX - rect.left, event.clientY - rect.top])
          const startPx = { x: event.clientX, y: event.clientY }
          let prev = { lng: origin.lng, lat: origin.lat }
          let moved = false
          dragFromRef.current.set(id, prev)
          plumeDragRef.current = {
            id,
            lng: origin.lng,
            lat: origin.lat,
            liveLng: origin.lng,
            liveLat: origin.lat,
            base: plumeFcRef.current,
          }
          const onDrag = (move: PointerEvent): void => {
            if (Math.hypot(move.clientX - startPx.x, move.clientY - startPx.y) > 3) moved = true
            const box = map.getCanvas().getBoundingClientRect()
            const at = map.unproject([move.clientX - box.left, move.clientY - box.top])
            const lng = origin.lng + (at.lng - start.lng)
            const lat = origin.lat + (at.lat - start.lat)
            created.setLngLat([lng, lat])
            if (plumeDragRef.current?.id === id) {
              plumeDragRef.current.liveLng = lng
              plumeDragRef.current.liveLat = lat
            }
            onMoveRef.current(id, lng, lat, prev)
            prev = { lng, lat }
            dragFromRef.current.set(id, prev)
            const drag = plumeDragRef.current
            if (drag && drag.id === id) {
              const dLng = lng - drag.lng
              const dLat = lat - drag.lat
              setGeo(
                map,
                SRC_PLUMES,
                plumesFollowingDrag(drag.base, plumeFcRef.current, id, dLng, dLat),
              )
            }
            // Emit Watch after the marker moves — trackPointer alone often sampled the
            // pre-move pose, so P's hand led the icon (or the icon never caught up).
            const now = performance.now()
            if (now - dragWatchEmitAtRef.current >= 32) {
              dragWatchEmitAtRef.current = now
              const center = map.getCenter()
              onCameraFrameRef.current?.({
                lng: center.lng,
                lat: center.lat,
                zoom: map.getZoom(),
                focusId: selectedIdRef.current ?? id,
                dragId: id,
                dragLng: lng,
                dragLat: lat,
                pointerLng: at.lng,
                pointerLat: at.lat,
                pointerDown: true,
                hoverId: id,
                grab: true,
                cursor: 'grabbing',
              })
            }
          }
          const onUp = (): void => {
            window.removeEventListener('pointermove', onDrag)
            window.removeEventListener('pointerup', onUp, true)
            window.removeEventListener('pointercancel', onUp, true)
            {
              const center = map.getCenter()
              const tip = created.getLngLat()
              onCameraFrameRef.current?.({
                lng: center.lng,
                lat: center.lat,
                zoom: map.getZoom(),
                focusId: selectedIdRef.current ?? id,
                dragId: id,
                dragLng: tip.lng,
                dragLat: tip.lat,
                pointerLng: tip.lng,
                pointerLat: tip.lat,
                pointerDown: true,
                hoverId: id,
                grab: true,
                cursor: 'grabbing',
              })
            }
            draggingIdsRef.current.delete(id)
            dragFromRef.current.delete(id)
            if (plumeDragRef.current?.id === id) plumeDragRef.current = null
            if (
          panWasEnabled &&
          !freehandRef.current &&
          !penRef.current &&
          !rulerRef.current &&
          !scribbleRef.current
        ) {
              map.dragPan.enable()
            }
            armSuppressLineClickRef.current()
            if (moved) onObjectMoveEndRef.current?.()
            else if (selectedIdRef.current !== id) {
              // Click (no drag) selects. A second click on the pin keeps the selection.
              armSuppressLineClickRef.current()
              onPipeRef.current(null)
              onSelectRef.current(id)
            }
          }
          window.addEventListener('pointermove', onDrag)
          window.addEventListener('pointerup', onUp, true)
          window.addEventListener('pointercancel', onUp, true)
        })
        markersRef.current.set(key, marker)
      } else {
        const live = plumeDragRef.current
        if (live && live.id === owner) {
          marker.setLngLat([live.liveLng, live.liveLat])
        } else if (!draggingIdsRef.current.has(owner)) {
          // Live playhead sampling owns keyframed markers; React props are lower-rate.
          const liveIso = samplePlayheadIsoRef.current?.() ?? null
          if (!(liveIso && owned.keyframes?.length)) {
            // Skip no-op writes — Mapbox subpixel-rounds setLngLat and a React↔RAF
            // fight (or 60fps same-pose writes) reads as ±1px jitter while Play awaits PreviewPlay.
            const cur = marker.getLngLat()
            if (
              Math.abs(cur.lng - stack.anchor.lng) > 1e-9 ||
              Math.abs(cur.lat - stack.anchor.lat) > 1e-9
            ) {
              marker.setLngLat([stack.anchor.lng, stack.anchor.lat])
            }
          }
        }
        if (marker.isDraggable()) marker.setDraggable(false)
        const markerEl = marker.getElement()
        const innerEl = markerEl.querySelector('.sc-mark')
        if (innerEl instanceof HTMLElement) innerEl.dataset.id = owner
      }
      const ownerFlash = flashing.has(owner)
      const textMark =
        owned.type === 'label' ||
        (owned.type === 'sketch' && objectLineSegments(owned).length === 0)
      if (iconsOnly && textMark) {
        marker.getElement().style.display = 'none'
        marker.getElement().style.opacity = ''
        continue
      }
      marker.getElement().style.display =
        isRep || ownerFlash || draggingIdsRef.current.has(owner) ? '' : 'none'
      if (!isRep && !ownerFlash && !draggingIdsRef.current.has(owner)) continue
          const stackSelected =
        (selectedId != null && stack.members.some((m) => m.id === selectedId)) ||
        (watchFocusId != null && stack.members.some((m) => m.id === watchFocusId))
      const stackRemote =
        remoteDragId != null && stack.members.some((m) => m.id === remoteDragId)
      const stackLit = stack.members.some((m) => lit.has(m.id))
      const face = stack.members.find((m) => m.id === owner) ?? active
      // Free-text labels: skip sel/lit rings — a green box around map copy steals focus.
      const faceIsTextLabel =
        face.type === 'label' ||
        (face.type === 'sketch' && objectLineSegments(face).length === 0)
      const paintArgs: MarkerPaintArgs = {
        active: isRep ? active : face,
        stack: stack.members,
        selected: stackSelected && !faceIsTextLabel,
        lit: stackLit && !faceIsTextLabel,
        title: (isRep ? active : face).label,
        linkHl: stackSelected && !faceIsTextLabel && linkHighlightSelectedRef.current,
        ghostFlash: flashing.has((isRep ? active : face).id),
        remote: stackRemote && !stackSelected,
      }
      markerPaintRef.current.set(key, paintArgs)
      const inner = marker.getElement().querySelector('.sc-mark')
      const flashHold = flashing.size > 0
      const dimMarker = flashHold && !paintArgs.ghostFlash
      marker.getElement().style.opacity = dimMarker ? '0.2' : ''
      if (inner instanceof HTMLElement) {
        const sig = [
          paintArgs.active.id,
          paintArgs.active.type,
          paintArgs.active.label,
          paintArgs.active.mapLabel ?? '',
          // params.color (e.g. airplane red after bird strike) must invalidate paint.
          typeof paintArgs.active.params.color === 'string'
            ? paintArgs.active.params.color.trim()
            : '',
          paintArgs.title,
          paintArgs.selected ? '1' : '0',
          paintArgs.lit ? '1' : '0',
          paintArgs.ghostFlash ? '1' : '0',
          paintArgs.linkHl ? '1' : '0',
          paintArgs.remote ? '1' : '0',
          String(Math.round(map.getZoom() * 2) / 2),
          paintArgs.stack.map((m) => `${m.id}:${m.label}`).join(','),
        ].join('|')
        if (inner.dataset.sig !== sig) {
          inner.dataset.sig = sig
          paintStackMarker(
            inner,
            paintArgs.active,
            paintArgs.stack,
            paintArgs.selected,
            paintArgs.lit,
            paintArgs.title,
            map.getZoom(),
            paintArgs.linkHl,
            paintArgs.ghostFlash,
            paintArgs.remote,
          )
        }
        if (inner.classList.contains('label')) inner.style.textShadow = dimMarker ? 'none' : ''
      }
    }
    for (const [id, marker] of markersRef.current) {
      if (keep.has(id)) continue
      marker.remove()
      markersRef.current.delete(id)
      markerPaintRef.current.delete(id)
    }
  }, [
    objects,
    selectedId,
    highlightIds,
    iconHighlightIds,
    styleEpoch,
    remoteDragId,
    watchFocusId,
    waypointObjectId,
    hideWaypointUnitIcon,
    linkHighlightSelected,
    ghostFlashIds,
    iconsOnly,
  ])

  // Live keyframe interpolation: sample playhead each frame, poseFromKeyframes → setLngLat.
  useEffect(() => {
    if (!samplePlayheadIso) return
    let raf = 0
    const tick = (): void => {
      const iso = samplePlayheadIsoRef.current?.() ?? null
      if (iso) {
        for (const obj of objectsRef.current) {
          if (!obj.keyframes?.length) continue
          if (draggingIdsRef.current.has(obj.id)) continue
          const pose = poseFromKeyframes(obj, iso)
          if (!pose) continue
          const marker = markersRef.current.get(`id:${obj.id}`)
          if (!marker) continue
          // Same pose every frame while Seek+Play freezes the head — repeated setLngLat
          // still jitters markers under Mapbox's CSS transform rounding.
          const cur = marker.getLngLat()
          if (
            Math.abs(cur.lng - pose.lng) > 1e-9 ||
            Math.abs(cur.lat - pose.lat) > 1e-9
          ) {
            marker.setLngLat([pose.lng, pose.lat])
          }
        }
      }
      raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [samplePlayheadIso])

  // Zoom: soft-clamp free-text size + icon-caption density.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const syncDensityChrome = (): void => {
      const z = map.getZoom()
      markersRef.current.forEach((marker, key) => {
        const args = markerPaintRef.current.get(key)
        const inner = marker.getElement().querySelector('.sc-mark')
        if (!(inner instanceof HTMLElement) || !args) return
        paintStackMarker(
          inner,
          args.active,
          args.stack,
          args.selected,
          args.lit,
          args.title,
          z,
          args.linkHl,
          args.ghostFlash,
          args.remote,
        )
      })
    }
    map.on('zoom', syncDensityChrome)
    map.on('zoomend', syncDensityChrome)
    syncDensityChrome()
    return () => {
      map.off('zoom', syncDensityChrome)
      map.off('zoomend', syncDensityChrome)
    }
  }, [styleEpoch])

  // Board line hover tip (sketches / closed roads have no pin marker).
  useEffect(() => {
    const map = mapRef.current
    const wrap = wrapRef.current
    if (!map || !wrap || !styleReadyRef.current) return
    if (!map.getLayer('sc-lines-hit')) return

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'sc-plume-tip',
      offset: 16,
    })
    const hide = (): void => {
      popup.remove()
    }
    const showAt = (text: string, lngLat: mapboxgl.LngLatLike): void => {
      popup.setLngLat(lngLat).setText(text).addTo(map)
    }
    const drawing = (): boolean =>
      freehandRef.current ||
      penRef.current ||
      rulerRef.current ||
      scribbleRef.current ||
      placeArmedRef.current
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      const box = map.getContainer()
      if (drawing() || overlayFillAt(map, ev.point) || box.classList.contains('plume-hover')) {
        if (overlayFillAt(map, ev.point)) box.classList.add('overlay-tip')
        hide()
        if (!strokeDragRef.current) map.getCanvas().style.cursor = ''
        return
      }
      if (!box.classList.contains('plume-hover')) box.classList.remove('overlay-tip')
      const id = nearestBoardLineId(map, ev.point, objectsRef.current, 16, true)
      if (!id) {
        const hovered = document.elementFromPoint(ev.originalEvent.clientX, ev.originalEvent.clientY)
        if (hovered instanceof Element && hovered.closest('.sc-mark')) return
        hide()
        if (!strokeDragRef.current) map.getCanvas().style.cursor = ''
        return
      }
      const obj = objectsRef.current.find((o) => o.id === id)
      const title =
        obj?.description?.trim() ||
        obj?.label?.trim() ||
        obj?.mapLabel?.trim() ||
        (obj ? paletteLabel(obj.type) : '')
      if (!title) {
        hide()
        return
      }
      if (obj?.type === 'sketch' && !strokeDragRef.current) map.getCanvas().style.cursor = 'grab'
      showAt(title, ev.lngLat)
    }
    const onWrapMove = (ev: MouseEvent): void => {
      const mark = (ev.target as HTMLElement | null)?.closest?.('.sc-mark') as HTMLElement | null
      if (!mark) return
      if (drawing()) {
        hide()
        return
      }
      underlayTipHiders.forEach((hideUnderlay) => hideUnderlay())
      const text = mark.dataset.tip?.trim()
      if (!text) {
        hide()
        return
      }
      const id = mark.dataset.id
      let lngLat: mapboxgl.LngLatLike | null = null
      if (id) {
        for (const [key, marker] of markersRef.current) {
          if (key === `id:${id}` || key.endsWith(id)) {
            lngLat = marker.getLngLat()
            break
          }
        }
      }
      if (!lngLat) return
      showAt(text, lngLat)
    }
    const onLeave = (): void => hide()

    map.on('mousemove', onMove)
    map.on('mouseout', onLeave)
    wrap.addEventListener('mousemove', onWrapMove)
    return () => {
      map.off('mousemove', onMove)
      map.off('mouseout', onLeave)
      wrap.removeEventListener('mousemove', onWrapMove)
      hide()
    }
  }, [styleEpoch])

  // Drag a line or sketch by the stroke. One commit on release — per-move React
  // updates were fighting the playhead redraw and leaving the old geometry behind.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current || !map.getLayer('sc-lines')) return
    let drag: {
      id: string
      startX: number
      startY: number
      startLng: number
      startLat: number
      lastLng: number
      lastLat: number
      moved: boolean
    } | null = null

    const shiftPt = (p: [number, number], dLng: number, dLat: number): [number, number] => [
      p[0] + dLng,
      p[1] + dLat,
    ]
    const preview = (id: string, dLng: number, dLat: number): void => {
      const shifted = objectsRef.current.map((obj) => {
        if (obj.id !== id) return obj
        const params = { ...obj.params }
        if (Array.isArray(params.segments)) {
          params.segments = (params.segments as [number, number][][]).map((seg) =>
            seg.map((p) => shiftPt(p, dLng, dLat)),
          )
        }
        return {
          ...obj,
          pose: obj.pose
            ? { lng: obj.pose.lng + dLng, lat: obj.pose.lat + dLat }
            : obj.pose,
          path: obj.path?.map((p) => shiftPt(p, dLng, dLat)),
          params,
        }
      })
      setGeo(
        map,
        SRC_LINES,
        lineFeatures(shifted, selectedIdRef.current, highlightIdsRef.current),
      )
    }
    const finish = (): void => {
      if (!drag) return
      const active = drag
      drag = null
      strokeDragRef.current = false
      if (!freehandRef.current && !scribbleRef.current) map.dragPan.enable()
      map.getCanvas().style.cursor = ''
      armSuppressLineClickRef.current()
      if (!active.moved) {
        onSelectRef.current(active.id)
        return
      }
      const obj = objectsRef.current.find((o) => o.id === active.id)
      const anchor = obj ? objectAnchorLngLat(obj) : null
      if (!anchor) return
      onMoveRef.current(
        active.id,
        anchor.lng + (active.lastLng - active.startLng),
        anchor.lat + (active.lastLat - active.startLat),
        anchor,
      )
      onObjectMoveEndRef.current?.()
    }
    const onDown = (ev: mapboxgl.MapMouseEvent): void => {
      const target = ev.originalEvent?.target
      if (target instanceof Element && target.closest('.sc-mark')) return
      if (
        !editingRef.current ||
        freehandRef.current ||
        penRef.current ||
        rulerRef.current ||
        scribbleRef.current ||
        placeArmedRef.current ||
        waypointObjectIdRef.current
      ) {
        return
      }
      const id = nearestBoardLineId(map, ev.point, objectsRef.current, 16)
      const obj = id ? objectsRef.current.find((o) => o.id === id) : undefined
      if (!id || obj?.type !== 'sketch') return
      const allowed = canDragObjectRef.current ? canDragObjectRef.current(id) : true
      if (!allowed) return
      drag = {
        id,
        startX: ev.point.x,
        startY: ev.point.y,
        startLng: ev.lngLat.lng,
        startLat: ev.lngLat.lat,
        lastLng: ev.lngLat.lng,
        lastLat: ev.lngLat.lat,
        moved: false,
      }
      strokeDragRef.current = true
      map.dragPan.disable()
      map.getCanvas().style.cursor = 'grabbing'
      ev.preventDefault()
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      if (!drag) return
      const dLng = ev.lngLat.lng - drag.startLng
      const dLat = ev.lngLat.lat - drag.startLat
      if (!drag.moved && Math.hypot(ev.point.x - drag.startX, ev.point.y - drag.startY) < 4) return
      drag.moved = true
      drag.lastLng = ev.lngLat.lng
      drag.lastLat = ev.lngLat.lat
      preview(drag.id, dLng, dLat)
    }
    map.on('mousedown', onDown)
    map.on('mousemove', onMove)
    map.on('mouseup', finish)
    window.addEventListener('mouseup', finish)
    return () => {
      map.off('mousedown', onDown)
      map.off('mousemove', onMove)
      map.off('mouseup', finish)
      window.removeEventListener('mouseup', finish)
      if (drag) finish()
    }
  }, [styleEpoch])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!freehandArmed) {
      draftPathRef.current = []
      if (!penArmed && !rulerArmed && map.getSource(SRC_DRAW)) {
        setGeo(map, SRC_DRAW, { type: 'FeatureCollection', features: [] })
      }
      // Don't steal pan back while Kermit scribble (or pen/ruler) is armed.
      if (!penArmed && !rulerArmed && !scribbleRef.current) map.dragPan.enable()
      return
    }
    map.dragPan.disable()
    const paintDraft = (): void => {
      const path = draftPathRef.current
      setGeo(map, SRC_DRAW, {
        type: 'FeatureCollection',
        features:
          path.length >= 2
            ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: path } }]
            : [],
      })
      if (map.getLayer('sc-draw')) {
        map.setPaintProperty('sc-draw', 'line-color', sketchStyleRef.current.color)
        map.setPaintProperty('sc-draw', 'line-width', sketchStyleRef.current.width)
        map.setPaintProperty('sc-draw', 'line-opacity', sketchStyleRef.current.opacity)
      }
    }
    const onDown = (ev: mapboxgl.MapMouseEvent): void => {
      const target = ev.originalEvent?.target
      if (target instanceof Element && target.closest('.sc-mark')) return
      draftPathRef.current = [[ev.lngLat.lng, ev.lngLat.lat]]
      paintDraft()
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      if (!draftPathRef.current.length) return
      draftPathRef.current = [...draftPathRef.current, [ev.lngLat.lng, ev.lngLat.lat]]
      paintDraft()
    }
    const onUp = (): void => {
      const path = draftPathRef.current
      draftPathRef.current = []
      paintDraft()
      if (path.length >= 2) onStrokeRef.current(path)
    }
    map.on('mousedown', onDown)
    map.on('mousemove', onMove)
    map.on('mouseup', onUp)
    return () => {
      map.off('mousedown', onDown)
      map.off('mousemove', onMove)
      map.off('mouseup', onUp)
      if (!scribbleRef.current && !penRef.current && !rulerRef.current) map.dragPan.enable()
    }
  }, [freehandArmed, penArmed, rulerArmed, sketchStyle.color, sketchStyle.width, sketchStyle.opacity])

  const scribbleLayerOn = scribbleVisible ?? scribbleArmed

  /** Paint committed personal scribbles (+ live draft while armed). Hidden = empty layer. */
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.getSource(SRC_SCRIBBLE)) return
    if (!scribbleLayerOn) {
      setGeo(map, SRC_SCRIBBLE, { type: 'FeatureCollection', features: [] })
      return
    }
    const features: GeoJSON.Feature[] = scribbleStrokes
      .filter((s) => s.path.length >= 2)
      .map((s) => ({
        type: 'Feature',
        properties: { id: s.id },
        geometry: { type: 'LineString', coordinates: s.path },
      }))
    const draft = scribbleDraftRef.current
    if (scribbleArmed && draft.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { id: '__draft' },
        geometry: { type: 'LineString', coordinates: draft },
      })
    }
    setGeo(map, SRC_SCRIBBLE, { type: 'FeatureCollection', features })
  }, [scribbleStrokes, scribbleArmed, scribbleLayerOn, styleEpoch])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!scribbleArmed) {
      scribbleDraftRef.current = []
      if (map.getSource(SRC_SCRIBBLE) && !scribbleLayerOn) {
        setGeo(map, SRC_SCRIBBLE, { type: 'FeatureCollection', features: [] })
      }
      map.getCanvas().style.cursor = ''
      if (!freehandArmed && !penArmed && !rulerArmed) map.dragPan.enable()
      return
    }
    map.dragPan.disable()
    map.getCanvas().style.cursor = 'crosshair'
    // Keep chalk above board lines / zones while drawing.
    if (map.getLayer('sc-scribble')) {
      try {
        map.moveLayer('sc-scribble')
      } catch {
        /* style still loading */
      }
    }
    const paintDraft = (): void => {
      if (!scribbleLayerOn) return
      const features: GeoJSON.Feature[] = scribbleStrokes
        .filter((s) => s.path.length >= 2)
        .map((s) => ({
          type: 'Feature',
          properties: { id: s.id },
          geometry: { type: 'LineString', coordinates: s.path },
        }))
      const path = scribbleDraftRef.current
      if (path.length >= 2) {
        features.push({
          type: 'Feature',
          properties: { id: '__draft' },
          geometry: { type: 'LineString', coordinates: path },
        })
      }
      setGeo(map, SRC_SCRIBBLE, { type: 'FeatureCollection', features })
    }
    const onDown = (ev: mapboxgl.MapMouseEvent): void => {
      const target = ev.originalEvent?.target
      if (target instanceof Element && target.closest('.sc-mark')) return
      ev.preventDefault()
      scribbleDraftRef.current = [[ev.lngLat.lng, ev.lngLat.lat]]
      paintDraft()
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      if (!scribbleDraftRef.current.length) return
      scribbleDraftRef.current = [...scribbleDraftRef.current, [ev.lngLat.lng, ev.lngLat.lat]]
      paintDraft()
    }
    const onUp = (): void => {
      const path = scribbleDraftRef.current
      scribbleDraftRef.current = []
      paintDraft()
      if (path.length >= 2) onScribbleStrokeRef.current?.(path)
    }
    map.on('mousedown', onDown)
    map.on('mousemove', onMove)
    map.on('mouseup', onUp)
    return () => {
      map.off('mousedown', onDown)
      map.off('mousemove', onMove)
      map.off('mouseup', onUp)
      map.getCanvas().style.cursor = ''
      if (!freehandRef.current && !penRef.current && !rulerRef.current) map.dragPan.enable()
    }
    // scribbleStrokes read live in paintDraft via closure — rebind when list grows
  }, [scribbleArmed, scribbleLayerOn, scribbleStrokes, freehandArmed, penArmed, rulerArmed])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!penArmed) {
      penDraftRef.current = []
      if (!freehandArmed && !rulerArmed && map.getSource(SRC_DRAW)) {
        setGeo(map, SRC_DRAW, { type: 'FeatureCollection', features: [] })
      }
      return
    }
    const paintPen = (cursor: [number, number] | null = null): void => {
      const pts = penDraftRef.current
      const features: GeoJSON.Feature[] = []
      if (pts.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: pts },
        })
      }
      if (pts.length >= 1 && cursor) {
        features.push({
          type: 'Feature',
          properties: { dash: true },
          geometry: { type: 'LineString', coordinates: [pts[pts.length - 1]!, cursor] },
        })
      }
      setGeo(map, SRC_DRAW, { type: 'FeatureCollection', features })
      if (map.getLayer('sc-draw')) {
        map.setPaintProperty('sc-draw', 'line-color', sketchStyleRef.current.color)
        map.setPaintProperty('sc-draw', 'line-width', sketchStyleRef.current.width)
        map.setPaintProperty('sc-draw', 'line-opacity', sketchStyleRef.current.opacity)
      }
    }
    const onClick = (ev: mapboxgl.MapMouseEvent): void => {
      if ((ev.originalEvent as MouseEvent | undefined)?.detail === 2) return
      penDraftRef.current = [...penDraftRef.current, [ev.lngLat.lng, ev.lngLat.lat]]
      paintPen()
    }
    const onDbl = (ev: mapboxgl.MapMouseEvent): void => {
      ev.preventDefault()
      const path = penDraftRef.current
      penDraftRef.current = []
      paintPen()
      if (path.length >= 2) onStrokeRef.current(path)
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      if (!penDraftRef.current.length) return
      paintPen([ev.lngLat.lng, ev.lngLat.lat])
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      penDraftRef.current = []
      paintPen()
    }
    map.on('click', onClick)
    map.on('dblclick', onDbl)
    map.on('mousemove', onMove)
    window.addEventListener('keydown', onKey)
    map.doubleClickZoom.disable()
    return () => {
      map.off('click', onClick)
      map.off('dblclick', onDbl)
      map.off('mousemove', onMove)
      window.removeEventListener('keydown', onKey)
      map.doubleClickZoom.enable()
    }
  }, [penArmed, freehandArmed, rulerArmed, sketchStyle.color, sketchStyle.width, sketchStyle.opacity])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!rulerArmed) {
      rulerDraftRef.current = []
      onRulerChangeRef.current?.(null)
      if (!freehandArmed && !penArmed && map.getSource(SRC_DRAW)) {
        setGeo(map, SRC_DRAW, { type: 'FeatureCollection', features: [] })
      }
      return
    }
    const publish = (): void => {
      const pts = rulerDraftRef.current
      let total = 0
      let last = 0
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!
        const b = pts[i]!
        last = haversineM(a[0], a[1], b[0], b[1])
        total += last
      }
      onRulerChangeRef.current?.(
        pts.length ? { points: pts.length, totalM: total, lastM: last } : null,
      )
    }
    const paintRuler = (cursor: [number, number] | null = null): void => {
      const pts = rulerDraftRef.current
      const features: GeoJSON.Feature[] = []
      if (pts.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: pts },
        })
      }
      if (pts.length >= 1 && cursor) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [pts[pts.length - 1]!, cursor] },
        })
      }
      setGeo(map, SRC_DRAW, { type: 'FeatureCollection', features })
      if (map.getLayer('sc-draw')) {
        map.setPaintProperty('sc-draw', 'line-color', '#ffffff')
        map.setPaintProperty('sc-draw', 'line-width', 2.5)
        map.setPaintProperty('sc-draw', 'line-opacity', 0.95)
      }
    }
    const onClick = (ev: mapboxgl.MapMouseEvent): void => {
      if ((ev.originalEvent as MouseEvent | undefined)?.detail === 2) return
      rulerDraftRef.current = [...rulerDraftRef.current, [ev.lngLat.lng, ev.lngLat.lat]]
      paintRuler()
      publish()
    }
    const onMove = (ev: mapboxgl.MapMouseEvent): void => {
      if (!rulerDraftRef.current.length) return
      paintRuler([ev.lngLat.lng, ev.lngLat.lat])
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      rulerDraftRef.current = []
      paintRuler()
      publish()
    }
    map.on('click', onClick)
    map.on('mousemove', onMove)
    window.addEventListener('keydown', onKey)
    return () => {
      map.off('click', onClick)
      map.off('mousemove', onMove)
      window.removeEventListener('keydown', onKey)
    }
  }, [rulerArmed, freehandArmed, penArmed])

  const drawArmed = freehandArmed || penArmed || rulerArmed

  const chipLng = mapAssetChip?.lng
  const chipLat = mapAssetChip?.lat
  useLayoutEffect(() => {
    const map = mapRef.current
    const el = assetChipRef.current
    if (!map || !el || chipLng == null || chipLat == null) return
    const place = (): void => {
      const p = map.project([chipLng, chipLat])
      el.style.left = `${p.x}px`
      el.style.top = `${p.y}px`
      el.style.transform =
        p.y < 72 ? 'translate(-50%, 12px)' : 'translate(-50%, calc(-100% - 12px))'
    }
    place()
    map.on('move', place)
    map.on('resize', place)
    return () => {
      map.off('move', place)
      map.off('resize', place)
    }
  }, [chipLng, chipLat, styleEpoch])

  return (
    <div
      className={`sc-mapwrap${placeArmed ? ' place-pick' : ''}${
        stylePaintReady ? '' : ' is-style-loading'
      }`}
    >
      <div
        ref={wrapRef}
        className={`sc-map${SHOW_MAPBOX_LOGO ? '' : ' hide-mapbox-logo'}${drawArmed ? ' sketch' : ''}${placeArmed ? ' place-pick' : ''}${focusDim ? ' focus-dim' : ''}${ghostFlashIds.length ? ' flash-hold' : ''}${iconsOnly ? ' icons-only' : ''}`}
      />
      {coordToast ? (
        <div className="sc-map-coord-toast" role="status">
          {coordToast}
        </div>
      ) : null}
      {mapAssetChip ? (
        <div ref={assetChipRef} className="sc-asset-chip" role="status">
          <div className="sc-asset-chip-copy">
            <strong>{mapAssetChip.name}</strong>
            {mapAssetChip.operator ? <span>{mapAssetChip.operator}</span> : null}
            {mapAssetChip.count > 1 ? (
              <span>{mapAssetChip.count} pipelines</span>
            ) : null}
          </div>
          <div className="sc-asset-chip-actions">
            <button type="button" className="sc-asset-chip-details" onClick={onMapAssetDetails}>
              Details
            </button>
            <button
              type="button"
              className="sc-asset-chip-x"
              aria-label="Dismiss map asset"
              onClick={onMapAssetDismiss}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}
      {pathHoldLabel ? (
        <div className="sc-map-hold-badge" role="status">
          {pathHoldLabel}
        </div>
      ) : null}
    </div>
  )
}, mapHostPropsEqual)
